"use client";

import Script from "next/script";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ChangeEvent,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
  type SetStateAction,
} from "react";
import * as XLSX from "xlsx";
import {
  budgetText,
  guestsText,
  loginText,
  registryText,
  systemText,
  type AppLanguage,
} from "./constants/dashboard-language";
import BudgetSection from "./components/BudgetSection";
import DashboardTopButtons from "./components/buttons/DashboardTopButtons";
import LanguageToggleButton from "./components/buttons/LanguageToggleButton";
import MusicToggleButton from "./components/buttons/MusicToggleButton";
import PaymentPackageSection from "./components/PaymentPackageSection";
import BudgetAmountModal from "./components/modals/BudgetAmountModal";
import BudgetDeleteModal from "./components/modals/BudgetDeleteModal";
import DeleteUploadedModal from "./components/modals/DeleteUploadedModal";
import GiftModal from "./components/modals/GiftModal";
import GuestsSection from "./components/GuestsSection";
import LandingPageSection from "./components/LandingPageSection";
import MapModal from "./components/modals/MapModal";
import RegistrySection from "./components/RegistrySection";
import SignOutConfirmModal from "./components/modals/SignOutConfirmModal";
import AttendanceStatusModal from "./components/modals/AttendanceStatusModal";
import {
  downloadRegistryExcelFile,
  filterRegistryGuestsByQuery,
} from "./utils/registry-export";

type GuestStatus = "Joining" | "Not yet" | "Maybe";
type GuestGroup = "ភ្ញៀវកូនស្រី" | "ភ្ញៀវកូនប្រុស";
type UploadedDraftGroupFilter = "all" | "bride" | "groom";
type GuestCategory = string;
type AttendanceFilter = "All" | GuestStatus;
type AttendanceGroupFilter = "All" | GuestGroup;
type AttendanceDoneFilter = "All" | "Pending" | "Done";
type AttendanceInviteFilter = "All" | "Invited" | "Not invited";
type AttendanceGiftFilter = "All" | "Gift" | "No gift";
type AttendanceHouseholdFilter = "all" | "single" | "family";
type AttendanceCategoryFilter = "all" | string;
type GiftCurrency = "USD" | "Riel";
type BudgetCurrency = GiftCurrency;
type GuestImportMode = "all" | "groom" | "bride";
type SameNameGiftFilter = "all" | "with_gift" | "no_gift";
type AttendanceGiftApiFilter = "all" | "with_gift" | "no_gift";

type Guest = {
  id: number;
  uploadedId?: number;
  name: string;
  phone: string;
  status: GuestStatus;
  group: GuestGroup;
  category: GuestCategory;
  notes: string;
  latitude: number | null;
  longitude: number | null;
  source?: "api" | "local";
  giftAmount?: number | null;
  giftCurrency?: GiftCurrency;
  invited?: boolean;
  done?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type UploadedBatch = {
  id: number;
  group: GuestGroup;
  createdAt: string;
  records: Guest[];
};

type Spending = {
  id: number;
  item: string;
  budget: number;
  budgetCurrency: BudgetCurrency;
  actual: number;
  actualCurrency: BudgetCurrency;
  note: string;
};

type LoginResponse = {
  success: boolean;
  message?: string;
  access_token?: string;
  token_type?: string;
  expires_in?: number;
};

type CategoryItem = {
  id: number;
  name: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
};

type RegistryDraftResponse = {
  id: number;
  guest_group: "bride" | "groom";
  name: string;
  category?: string | null;
  notes?: string | null;
  latitude: number | null;
  longitude: number | null;
  gift_amount?: string | number | null;
  gift_currency?: GiftCurrency | "riel" | "usd" | "RIEL" | "Riels" | null;
  invited?: boolean;
  is_done?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

type RegistryDraftListResponse = {
  items: RegistryDraftResponse[];
  total: number;
  page?: number;
  limit?: number;
  total_pages?: number;
};

type GuestsListItemResponse = {
  id: number;
  name: string;
  category: string;
  guest_group: "bride" | "groom";
  latitude: number | null;
  longitude: number | null;
  gift_amount: string | number | null;
  gift_currency: GiftCurrency | "riel" | "usd" | "RIEL" | "Riels" | null;
  invited: boolean;
  done?: boolean;
  is_done?: boolean;
};

type GuestsListResponse = {
  items: GuestsListItemResponse[];
  total: number;
  invited_count: number;
  with_gift_count?: number;
  no_gift_count?: number;
  page?: number;
  limit?: number;
  total_pages?: number;
};

type GuestGiftImportResponse = {
  updated_count: number;
  failed_count: number;
  errors: Array<{
    row: number;
    message: string;
  }>;
};

type GuestsSearchByNameResponse = {
  items: GuestsListItemResponse[];
  total: number;
  invited_count?: number;
  with_gift_count?: number;
  no_gift_count?: number;
  page?: number;
  limit?: number;
  total_pages?: number;
};

type GuestsCountResponse = {
  total: number;
  invited_count: number;
  with_gift_count?: number;
};

type ParsedGuestImportRow = {
  name: string;
  group: GuestGroup | null;
  hasGift: boolean;
  giftAmount: number | null;
  giftCurrency: GiftCurrency;
};

type BudgetItemResponse = {
  id: number;
  item: string;
  plan_amount: string | number;
  plan_currency?: BudgetCurrency | "usd" | "riel" | "RIEL" | "Riels" | null;
  current_amount: string | number;
  current_currency?: BudgetCurrency | "usd" | "riel" | "RIEL" | "Riels" | null;
  left_amount: string | number;
  notes: string | null;
};

type BudgetTotalsResponse = {
  budget_cap: string | number;
  plan_amount_total: string | number;
  current_amount_total: string | number;
  left_amount: string | number;
  total_usd: string | number;
  total_riel: string | number;
  plan_total_usd?: string | number;
  plan_total_riel?: string | number;
  current_total_usd?: string | number;
  current_total_riel?: string | number;
  left_total_usd?: string | number;
  left_total_riel?: string | number;
};

type BudgetPageResponse = {
  items: BudgetItemResponse[];
  totals: BudgetTotalsResponse;
};
type BudgetItemImportResponse = {
  created_count: number;
  failed_count: number;
  items: BudgetItemResponse[];
  errors: Array<{
    row: number;
    message: string;
  }>;
};
type GuestEstimateResponse = {
  total_found: number;
  bride_total: number;
  groom_total: number;
  estimated_guest_total: number;
  guests_per_table: number;
  estimated_table_count: number;
};

type MapCoords = {
  lat: number;
  lng: number;
};

type GoogleMapsMarker = {
  setPosition: (coords: MapCoords) => void;
};

type GoogleMapsMap = {
  setCenter: (coords: MapCoords) => void;
  setZoom: (zoom: number) => void;
  addListener: (
    eventName: "click",
    handler: (event: { latLng: { lat: () => number; lng: () => number } }) => void,
  ) => void;
};

type GoogleMapsApi = {
  maps?: {
    Map: new (
      element: HTMLDivElement,
      options: {
        center: MapCoords;
        zoom: number;
        disableDefaultUI: boolean;
        zoomControl: boolean;
      },
    ) => GoogleMapsMap;
    Marker: new (options: { position: MapCoords; map: GoogleMapsMap }) => GoogleMapsMarker;
  };
};

const API_AUTH_LOGIN_PATH =
  process.env.NEXT_PUBLIC_WEDDING_AUTH_LOGIN_PATH ?? "/wedding/auth/login";
const API_AUTH_ME_PATH =
  process.env.NEXT_PUBLIC_WEDDING_AUTH_ME_PATH ?? "/wedding/auth/me";
const API_AUTH_LOGOUT_PATH =
  process.env.NEXT_PUBLIC_WEDDING_AUTH_LOGOUT_PATH ?? "/wedding/auth/logout";
const API_CATEGORIES_PATH =
  process.env.NEXT_PUBLIC_WEDDING_CATEGORIES_PATH ?? "/wedding/categories";
const API_REGISTRY_DRAFTS_PATH =
  process.env.NEXT_PUBLIC_WEDDING_REGISTRY_DRAFTS_PATH ?? "/wedding/registry/drafts";
const API_GUESTS_SEARCH_PATH =
  process.env.NEXT_PUBLIC_WEDDING_GUESTS_SEARCH_PATH ?? "/wedding/guests/search-box";
const API_GUESTS_SEARCH_NAME_PATH =
  process.env.NEXT_PUBLIC_WEDDING_GUESTS_SEARCH_NAME_PATH ?? "/wedding/guests/same-name";
const API_GUESTS_PATH =
  process.env.NEXT_PUBLIC_WEDDING_GUESTS_PATH ?? "/wedding/guests";
const API_GUESTS_HOUSEHOLD_PATH =
  process.env.NEXT_PUBLIC_WEDDING_GUESTS_HOUSEHOLD_PATH ?? "/wedding/guests/household";
const API_GUESTS_COUNT_PATH =
  process.env.NEXT_PUBLIC_WEDDING_GUESTS_COUNT_PATH ?? "/wedding/guests/count";
const API_GUESTS_ESTIMATE_PATH =
  process.env.NEXT_PUBLIC_WEDDING_GUESTS_ESTIMATE_PATH ?? "/wedding/guests/estimate";
const WEDDING_THEME_SONG_URL =
  "https://cdn.theapka.com/music/Kou_Pi_Preng_Prutt-Him_Sivorn_Ft_Ek_Side.mp3";
const API_GUEST_GIFTS_IMPORT_PATH =
  process.env.NEXT_PUBLIC_WEDDING_GUEST_GIFTS_IMPORT_PATH ?? "/wedding/registry/uploaded/gifts/import-excel";
const REGISTRY_TABLE_PAGE = 1;
const REGISTRY_TABLE_LIMIT = 150;
const ATTENDANCE_TABLE_LIMIT = 150;
const ATTENDANCE_EXPORT_LIMIT = 200;
const ATTENDANCE_EXPORT_CONCURRENCY = 4;
const API_BUDGET_PAGE_PATH =
  process.env.NEXT_PUBLIC_WEDDING_BUDGET_PAGE_PATH ?? "/wedding/budget";
const API_BUDGET_ITEMS_PATH =
  process.env.NEXT_PUBLIC_WEDDING_BUDGET_ITEMS_PATH ?? "/wedding/budget/items";
const API_BUDGET_ITEMS_IMPORT_TEMPLATE_PATH =
  process.env.NEXT_PUBLIC_WEDDING_BUDGET_ITEMS_IMPORT_TEMPLATE_PATH ??
  "/wedding/budget/items/import-excel/template";
const API_BUDGET_ITEMS_IMPORT_PATH =
  process.env.NEXT_PUBLIC_WEDDING_BUDGET_ITEMS_IMPORT_PATH ??
  "/wedding/budget/items/import-excel";
const API_BUDGET_TOTALS_PATH =
  process.env.NEXT_PUBLIC_WEDDING_BUDGET_TOTALS_PATH ?? "/wedding/budget/totals";
const CATEGORY_TRANSLATIONS_STORAGE_KEY = "wedding_category_translations";
const AUTH_TOKEN_STORAGE_KEY = "wedding_access_token";
const AUTH_TOKEN_TYPE_STORAGE_KEY = "wedding_token_type";
const AUTH_TOKEN_EXPIRES_IN_STORAGE_KEY = "wedding_token_expires_in";
const AUTH_TOKEN_EXPIRES_AT_STORAGE_KEY = "wedding_token_expires_at";
const DASHBOARD_ACTIVE_TAB_STORAGE_KEY = "wedding_active_dashboard_tab";
const WEDDING_API_BASE =
  process.env.NEXT_PUBLIC_WEDDING_API_BASE?.trim().replace(/\/$/, "") || "/api";

const buildApiUrl = (path: string) =>
  `${WEDDING_API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

const paginateRows = <T,>(rows: T[], page: number, limit: number) => {
  const totalPages = rows.length > 0 ? Math.ceil(rows.length / limit) : 1;
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * limit;
  return {
    items: rows.slice(start, start + limit),
    page: safePage,
    totalPages,
  };
};

const clearStoredAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_TYPE_STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_EXPIRES_IN_STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_EXPIRES_AT_STORAGE_KEY);
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const payload = token.split(".")[1];
  if (!payload) {
    return null;
  }

  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padding = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
    const decoded = atob(`${normalized}${padding}`);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const getStoredTokenExpiresAt = (token: string | null) => {
  if (!token) {
    return 0;
  }

  const payload = decodeJwtPayload(token);
  const jwtExpiresAt = typeof payload?.exp === "number" ? payload.exp * 1000 : 0;
  const storedExpiresAt = Number(localStorage.getItem(AUTH_TOKEN_EXPIRES_AT_STORAGE_KEY));
  const validExpirations = [jwtExpiresAt, storedExpiresAt].filter(
    (value) => Number.isFinite(value) && value > 0,
  );

  return validExpirations.length > 0 ? Math.min(...validExpirations) : 0;
};

const isStoredTokenValid = (token: string | null) =>
  getStoredTokenExpiresAt(token) > Date.now();

const DEFAULT_CATEGORY_ITEMS: CategoryItem[] = [
  {
    id: 1,
    name: "Friend",
    is_default: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: 2,
    name: "Relatives",
    is_default: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: 3,
    name: "Colleagues",
    is_default: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: 4,
    name: "Management",
    is_default: true,
    created_at: "",
    updated_at: "",
  },
];

const GROUP_TO_API: Record<GuestGroup, "bride" | "groom"> = {
  "ភ្ញៀវកូនស្រី": "bride",
  "ភ្ញៀវកូនប្រុស": "groom",
};
const GROUP_FROM_API: Record<"bride" | "groom", GuestGroup> = {
  bride: "ភ្ញៀវកូនស្រី",
  groom: "ភ្ញៀវកូនប្រុស",
};
const STATUS_TO_API: Record<GuestStatus, "joining" | "maybe" | "not_yet"> = {
  Joining: "joining",
  Maybe: "maybe",
  "Not yet": "not_yet",
};
const STATUS_FROM_API: Record<"joining" | "maybe" | "not_yet", GuestStatus> = {
  joining: "Joining",
  maybe: "Maybe",
  not_yet: "Not yet",
};

const tabs = [
  { id: "registry", label: "Registry" },
  { id: "attendance", label: "Guests" },
  { id: "budget", label: "Budget" },
  { id: "payment-package", label: "Payment Package" },
  { id: "landing-page", label: "Landing Page" },
] as const;
const visibleTabs = tabs.filter((tab) => tab.id !== "landing-page");
export type DashboardTab = (typeof tabs)[number]["id"];

const isDashboardTab = (value: string | null): value is DashboardTab =>
  tabs.some((tab) => tab.id === value);

const BUDGET_CAP = 10000;
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const normalizeGiftCurrency = (
  value: GiftCurrency | "riel" | "usd" | "RIEL" | "Riels" | string | null | undefined,
): GiftCurrency => {
  if (
    typeof value === "string" &&
    (value.toLowerCase() === "riel" || value.toLowerCase() === "riels")
  ) {
    return "Riel";
  }
  return "USD";
};

const toApiGiftCurrency = (
  value: GiftCurrency | "riel" | "usd" | "RIEL" | "Riels" | null | undefined,
) => {
  return normalizeGiftCurrency(value) === "Riel" ? "Riels" : "USD";
};

const mapBudgetItemToSpending = (
  item: BudgetItemResponse,
  fallbackBudgetCurrency: BudgetCurrency = "USD",
  fallbackActualCurrency: BudgetCurrency = "USD",
): Spending => ({
  id: item.id,
  item: item.item,
  budget: Number(item.plan_amount ?? 0) || 0,
  budgetCurrency:
    item.plan_currency != null
      ? normalizeGiftCurrency(item.plan_currency)
      : fallbackBudgetCurrency,
  actual: Number(item.current_amount ?? 0) || 0,
  actualCurrency:
    item.current_currency != null
      ? normalizeGiftCurrency(item.current_currency)
      : fallbackActualCurrency,
  note: item.notes ?? "",
});

const normalizeImportKey = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const IMPORT_HEADER_ALIASES = {
  name: ["guest name", "guest_name", "name"],
  group: ["group guest", "guest group", "group_guest", "guest_group", "group"],
  haveGift: ["gift", "have gift", "has gift", "have_gift", "has_gift", "gift amount", "gift_amount"],
  currency: ["currency", "gift currency", "gift_currency"],
  usd: ["usd", "gift usd", "amount usd"],
  khr: ["khr", "riel", "riels", "gift khr", "gift riel", "amount khr", "amount riel"],
} as const;

const getImportCellValue = (
  row: Record<string, unknown>,
  aliases: readonly string[],
) => {
  const rowEntries = Object.entries(row);
  for (const alias of aliases) {
    const matched = rowEntries.find(([key]) => normalizeImportKey(key) === normalizeImportKey(alias));
    if (matched) {
      return matched[1];
    }
  }
  return "";
};

const findRowValueByParser = <T,>(
  row: Record<string, unknown>,
  parser: (value: unknown) => T | null,
) => {
  for (const value of Object.values(row)) {
    const parsed = parser(value);
    if (parsed != null) {
      return parsed;
    }
  }
  return null;
};

//test

const parseImportBoolean = (value: unknown) => {
  const normalized = normalizeImportKey(value);
  if (!normalized) return null;
  if (["yes", "y", "true", "1", "gift", "has gift", "មាន", "មានចំណងដៃ"].includes(normalized)) {
    return true;
  }
  if (["no", "n", "false", "0", "no gift", "គ្មាន", "គ្មានចំណងដៃ"].includes(normalized)) {
    return false;
  }
  return null;
};

const parseImportAmount = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value !== "string") {
    return 0;
  }
  const normalized = value.replace(/,/g, "").replace(/[^\d.-]/g, "").trim();
  if (!normalized) return 0;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseImportGuestGroup = (value: unknown): GuestGroup | null => {
  const normalized = normalizeImportKey(value);
  if (!normalized) return null;
  if (["bride", "bride guest", "guest bride", "ភ្ញៀវកូនស្រី", "កូនស្រី"].includes(normalized)) {
    return "ភ្ញៀវកូនស្រី";
  }
  if (
    ["groom", "groom guest", "guest groom", "ភ្ញៀវកូនប្រុស", "កូនប្រុស", "ប្រុស"].includes(
      normalized,
    )
  ) {
    return "ភ្ញៀវកូនប្រុស";
  }
  if (["ស្រី"].includes(normalized)) {
    return "ភ្ញៀវកូនស្រី";
  }
  return null;
};

const isGuestImportHeaderRow = (row: unknown[]) => {
  return row.some((cell) => {
    const normalized = normalizeImportKey(cell);
    return [
      ...IMPORT_HEADER_ALIASES.name,
      ...IMPORT_HEADER_ALIASES.group,
      ...IMPORT_HEADER_ALIASES.haveGift,
      ...IMPORT_HEADER_ALIASES.currency,
      ...IMPORT_HEADER_ALIASES.usd,
      ...IMPORT_HEADER_ALIASES.khr,
    ].some((alias) => normalizeImportKey(alias) === normalized);
  });
};

const parseGuestImportWorkbook = async (
  file: File,
  mode: GuestImportMode,
): Promise<ParsedGuestImportRow[]> => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error("The Excel file is empty.");
  }

  const sheet = workbook.Sheets[firstSheetName];
  const rawMatrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: "",
    blankrows: false,
  });
  if (rawMatrix.length === 0) {
    throw new Error("The Excel file has no guest rows.");
  }

  const importedRows: ParsedGuestImportRow[] = [];
  const errors: string[] = [];
  const forcedGroup =
    mode === "groom" ? "ភ្ញៀវកូនប្រុស" : mode === "bride" ? "ភ្ញៀវកូនស្រី" : null;
  const hasHeaderRow = rawMatrix.length > 0 && isGuestImportHeaderRow(rawMatrix[0]);

  if (hasHeaderRow) {
    const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });

    rawRows.forEach((row, index) => {
      const rowNumber = index + 2;
      const rawName = getImportCellValue(row, IMPORT_HEADER_ALIASES.name);
      const rawGroup = getImportCellValue(row, IMPORT_HEADER_ALIASES.group);
      const rawHaveGift = getImportCellValue(row, IMPORT_HEADER_ALIASES.haveGift);
      const rawCurrency = getImportCellValue(row, IMPORT_HEADER_ALIASES.currency);
      const rawUsd = getImportCellValue(row, IMPORT_HEADER_ALIASES.usd);
      const rawKhr = getImportCellValue(row, IMPORT_HEADER_ALIASES.khr);
      const name = String(rawName ?? "").trim();
      const usd = parseImportAmount(rawUsd);
      const khr = parseImportAmount(rawKhr);
      const hasGift = parseImportBoolean(rawHaveGift);
      const explicitCurrency = normalizeGiftCurrency(
        typeof rawCurrency === "string" ? rawCurrency : String(rawCurrency ?? ""),
      );
      const hasCurrencyValue = String(rawCurrency ?? "").trim().length > 0;
      const isBlankRow =
        !name &&
        !String(rawGroup ?? "").trim() &&
        !String(rawHaveGift ?? "").trim() &&
        !String(rawCurrency ?? "").trim() &&
        usd === 0 &&
        khr === 0;

      if (isBlankRow) {
        return;
      }

      if (!name) {
        errors.push(`Row ${rowNumber}: guest name is required.`);
        return;
      }

      const group =
        forcedGroup ??
        parseImportGuestGroup(rawGroup) ??
        findRowValueByParser(row, parseImportGuestGroup) ??
        null;

      const giftAmountFromGiftColumn = parseImportAmount(rawHaveGift);
      const resolvedUsd =
        usd > 0 ? usd : normalizeImportKey(rawCurrency) === "usd" ? giftAmountFromGiftColumn : 0;
      const resolvedKhr =
        khr > 0
          ? khr
          : usd === 0 && !hasCurrencyValue && giftAmountFromGiftColumn > 0
            ? giftAmountFromGiftColumn
            : ["khr", "riel", "riels", "៛"].includes(normalizeImportKey(rawCurrency))
              ? giftAmountFromGiftColumn
              : 0;

      if (resolvedUsd < 0 || resolvedKhr < 0) {
        errors.push(`Row ${rowNumber}: USD and KHR must be zero or greater.`);
        return;
      }

      if (resolvedUsd > 0 && resolvedKhr > 0) {
        errors.push(`Row ${rowNumber}: fill only one gift currency, USD or KHR.`);
        return;
      }

      const resolvedHasGift =
        hasGift ?? (giftAmountFromGiftColumn > 0 || resolvedUsd > 0 || resolvedKhr > 0);

      if (resolvedHasGift === false && (hasCurrencyValue || resolvedUsd > 0 || resolvedKhr > 0)) {
        importedRows.push({
          name,
          group,
          hasGift: false,
          giftAmount: null,
          giftCurrency: "Riel",
        });
        return;
      }

      const giftCurrency =
        resolvedUsd > 0
          ? "USD"
          : resolvedKhr > 0
            ? "Riel"
            : hasCurrencyValue
              ? explicitCurrency
              : "Riel";

      importedRows.push({
        name,
        group,
        hasGift: resolvedHasGift === true,
        giftAmount:
          resolvedHasGift === true
            ? resolvedUsd > 0
              ? resolvedUsd
              : resolvedKhr > 0
                ? resolvedKhr
                : 0
            : null,
        giftCurrency,
      });
    });
  } else {
    rawMatrix.forEach((row, index) => {
      const rowNumber = index + 1;
      const name = String(row[0] ?? "").trim();
      const rawAmount = row[1];
      const rawCurrency = row[2];
      const rawGroup = row[3];
      const amount = parseImportAmount(rawAmount);
      const currencyText = String(rawCurrency ?? "").trim();
      const group = forcedGroup ?? parseImportGuestGroup(rawGroup) ?? null;
      const isBlankRow =
        !name && String(rawAmount ?? "").trim() === "" && !currencyText && !String(rawGroup ?? "").trim();

      if (isBlankRow) {
        return;
      }

      if (!name) {
        errors.push(`Row ${rowNumber}: guest name is required.`);
        return;
      }

      let giftCurrency: GiftCurrency = "Riel";
      if (currencyText) {
        const normalizedCurrency = normalizeImportKey(currencyText);
        if (["usd"].includes(normalizedCurrency)) {
          giftCurrency = "USD";
        } else if (["khr", "riel", "riels", "៛"].includes(normalizedCurrency)) {
          giftCurrency = "Riel";
        } else {
          errors.push(`Row ${rowNumber}: currency must be USD or KHR.`);
          return;
        }
      } else if (amount > 0) {
        giftCurrency = "Riel";
      }

      importedRows.push({
        name,
        group,
        hasGift: amount > 0,
        giftAmount: amount > 0 ? amount : null,
        giftCurrency,
      });
    });
  }

  if (errors.length > 0) {
    throw new Error(errors.slice(0, 5).join(" "));
  }

  if (importedRows.length === 0) {
    throw new Error("No valid guest rows were found in the Excel file.");
  }

  return importedRows;
};

const SAMPLE_GUESTS: Guest[] = [];
const TELEGRAM_OWNER_USERNAME = "v_sachakborameiy";
const PROJECT_ACCESS_PAYMENT_URL =
  "https://acledabank.com.kh/acleda?payment_data=qWY5B2SAUfIhLblxzOtfu7MoGiCJZ9tD6xCEaMjpHskjsBq86pQAS2o75TTR7IUAN6uVYO/5wOLMm1GcChofOvqxFDn4AGbhlL4IdGSLHm0ZmzK7u+/ZSqLLmrR+r04i0a7xoTmW5f3T7g1jJF4Fa5VMvmN4Q2bB7NxtJ15l2e+RH6Oyp8vpUS5wPOs5Vrv8nV8Po/CL1yW6YYjOXjUCyK1DA6vXsKM8ICeM4OUuIRQZA6BFQ7pMYw86sIbeOhlfrCTpIl1y0C+SdyrmZCqqOwPYMuGGZ9r93Xy/gKXKPP9q4F3MMnhaSTxmkFAy3Orc&key=khqr";

export default function WeddingDashboard() {
  const mapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY?.trim() ?? "";
  const hasMapsApiKey = mapsApiKey.length > 0;
  const [isDark, setIsDark] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [language, setLanguage] = useState<AppLanguage>("en");
  const [isAuthed, setIsAuthed] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [isAccessRequestPageOpen, setIsAccessRequestPageOpen] = useState(false);
  const [sampleFullName, setSampleFullName] = useState("");
  const [sampleContact, setSampleContact] = useState("");
  const [projectFullName, setProjectFullName] = useState("");
  const [projectContact, setProjectContact] = useState("");
  const [sampleCredentialsUnlocked, setSampleCredentialsUnlocked] = useState(false);
  const [sampleRequestNotice, setSampleRequestNotice] = useState("");
  const [isSendingSampleRequest, setIsSendingSampleRequest] = useState(false);
  const [paymentPopupOpen, setPaymentPopupOpen] = useState(false);
  const [paymentPopupTab, setPaymentPopupTab] = useState<"scan" | "upload">("scan");
  const [paymentScreenshotName, setPaymentScreenshotName] = useState("");
  const [paymentScreenshotFile, setPaymentScreenshotFile] = useState<File | null>(null);
  const [isSendingPaymentScreenshot, setIsSendingPaymentScreenshot] = useState(false);
  const [paymentSendNotice, setPaymentSendNotice] = useState("");
  const [projectRequiredNotice, setProjectRequiredNotice] = useState("");
  const [activeTab, setActiveTab] = useState<DashboardTab>("registry");
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [attendanceFilter, setAttendanceFilter] = useState<AttendanceFilter>("All");
  const [attendanceGroupFilter, setAttendanceGroupFilter] =
    useState<AttendanceGroupFilter>("All");
  const [attendanceDoneFilter, setAttendanceDoneFilter] =
    useState<AttendanceDoneFilter>("All");
  const [attendanceInviteFilter, setAttendanceInviteFilter] =
    useState<AttendanceInviteFilter>("All");
  const [attendanceGiftFilter, setAttendanceGiftFilter] =
    useState<AttendanceGiftFilter>("All");
  const [attendanceHouseholdFilter, setAttendanceHouseholdFilter] =
    useState<AttendanceHouseholdFilter>("all");
  const [attendanceCategoryFilter, setAttendanceCategoryFilter] =
    useState<AttendanceCategoryFilter>("all");
  const [categoryItems, setCategoryItems] =
    useState<CategoryItem[]>(DEFAULT_CATEGORY_ITEMS);
  const categories = useMemo(
    () => categoryItems.map((item) => item.name),
    [categoryItems],
  );
  const [lastSelectedRegistryCategory, setLastSelectedRegistryCategory] = useState("");
  const [attendanceDoneIds, setAttendanceDoneIds] = useState<number[]>([]);
  const [isUploadingDrafts, setIsUploadingDrafts] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [duplicateUploadNames, setDuplicateUploadNames] = useState<string[]>([]);
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [guests, setGuests] = useState<Guest[]>(SAMPLE_GUESTS);
  const [attendanceGuests, setAttendanceGuests] = useState<Guest[]>([]);
  const [attendanceDataMode, setAttendanceDataMode] = useState<"default" | "same-name-have-gift">("default");
  const [attendanceSameNameRows, setAttendanceSameNameRows] = useState<Guest[]>([]);
  const [attendanceCounts, setAttendanceCounts] = useState<GuestsCountResponse>({
    total: 0,
    invited_count: 0,
    with_gift_count: 0,
  });
  const [attendanceGroupCounts, setAttendanceGroupCounts] = useState<{
    all: number;
    bride: number;
    groom: number;
  }>({
    all: 0,
    bride: 0,
    groom: 0,
  });
  const [isLoadingAttendanceGuests, setIsLoadingAttendanceGuests] = useState(false);
  const [isDownloadingAllGuests, setIsDownloadingAllGuests] = useState(false);
  const [isLoadingGuestsWithGift, setIsLoadingGuestsWithGift] = useState(false);
  const [isDownloadingGuestsWithGift, setIsDownloadingGuestsWithGift] = useState(false);
  const [isUploadingGuestExcel, setIsUploadingGuestExcel] = useState(false);
  const [guestEstimate, setGuestEstimate] = useState<{
    totalFound: number;
    brideTotal: number;
    groomTotal: number;
    estimatedGuestTotal: number;
    guestsPerTable: number;
    estimatedTableCount: number;
  } | null>(null);
  const [isLoadingGuestEstimate, setIsLoadingGuestEstimate] = useState(false);
  const [guestEstimateError, setGuestEstimateError] = useState("");
  const [registrySearch, setRegistrySearch] = useState("");
  const [registryDataMode, setRegistryDataMode] = useState<"drafts" | "same-name">("drafts");
  const [registrySameNameGiftFilter, setRegistrySameNameGiftFilter] = useState<SameNameGiftFilter>("with_gift");
  const [registrySameNameRows, setRegistrySameNameRows] = useState<Guest[]>([]);
  const [sameNameGiftCounts, setSameNameGiftCounts] = useState({
    withGift: 0,
    noGift: 0,
  });
  const [uploadedBatches, setUploadedBatches] = useState<UploadedBatch[]>([]);
  const [showUploadedTable, setShowUploadedTable] = useState(true);
  const [uploadedGroupFilter, setUploadedGroupFilter] =
    useState<UploadedDraftGroupFilter>("all");
  const [uploadedGuests, setUploadedGuests] = useState<Guest[]>([]);
  const [uploadedGuestsPage, setUploadedGuestsPage] = useState(1);
  const [uploadedGuestsTotalPages, setUploadedGuestsTotalPages] = useState(1);
  const [uploadedGuestCounts, setUploadedGuestCounts] = useState<{
    all: number;
    bride: number;
    groom: number;
  }>({
    all: 0,
    bride: 0,
    groom: 0,
  });
  const [isLoadingUploadedGuests, setIsLoadingUploadedGuests] = useState(false);
  const [uploadedGuestsError, setUploadedGuestsError] = useState("");
  const [uploadedDraftsReloadKey, setUploadedDraftsReloadKey] = useState(0);
  const [registryUpdateResult, setRegistryUpdateResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  useEffect(() => {
    if (activeTab === "registry") {
      setShowUploadedTable(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (registryUpdateResult?.type !== "success") {
      return;
    }
    const timer = window.setTimeout(() => {
      setRegistryUpdateResult(null);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [registryUpdateResult]);
  useEffect(() => {
    if (duplicateUploadNames.length === 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setDuplicateUploadNames([]);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [duplicateUploadNames]);
  const [uploadedDeleteId, setUploadedDeleteId] = useState<number | null>(null);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [isDeletingSelectedUploadedRows, setIsDeletingSelectedUploadedRows] = useState(false);
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendanceTotalPages, setAttendanceTotalPages] = useState(1);
  const [attendanceViewStatus, setAttendanceViewStatus] = useState<GuestStatus | null>(null);
  const [giftGuestId, setGiftGuestId] = useState<number | null>(null);
  const [giftAmountInput, setGiftAmountInput] = useState("");
  const [giftCurrencyInput, setGiftCurrencyInput] = useState<GiftCurrency>("Riel");
  const [giftInputError, setGiftInputError] = useState("");
  const [nameErrorIds, setNameErrorIds] = useState<number[]>([]);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapGuestId, setMapGuestId] = useState<number | null>(null);
  const [locationError, setLocationError] = useState("");
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<GoogleMapsMap | null>(null);
  const markerRef = useRef<GoogleMapsMarker | null>(null);
  const weddingMusicRef = useRef<HTMLAudioElement | null>(null);
  const guestIdSeedRef = useRef<number>(Date.now());
  const uploadedGuestsRequestIdRef = useRef(0);
  const uploadedDraftsRequestSignatureRef = useRef<string | null>(null);
  const attendanceRequestSignatureRef = useRef<string | null>(null);
  const attendanceGuestsRequestIdRef = useRef(0);
  const authValidationRequestRef = useRef<Promise<boolean> | null>(null);
  const validatedInteractionReplayRef = useRef<WeakSet<HTMLElement>>(new WeakSet());
  const uploadedDraftPageRequestsRef = useRef<Map<string, Promise<RegistryDraftListResponse>>>(
    new Map(),
  );
  const uploadedDraftPageCacheRef = useRef<
    Map<string, { expiresAt: number; payload: RegistryDraftListResponse }>
  >(new Map());
  const [spendingForm, setSpendingForm] = useState({
    item: "",
    budget: "",
    budgetCurrency: "USD" as BudgetCurrency,
    actual: "",
    actualCurrency: "USD" as BudgetCurrency,
    note: "",
  });
  const [budgetAmountModalOpen, setBudgetAmountModalOpen] = useState(false);
  const [isUploadingBudgetFile, setIsUploadingBudgetFile] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState({
    budget: "",
    actual: "",
  });
  const [budgetTotalsOverride, setBudgetTotalsOverride] = useState<{
    budgetTotal: number;
    actualTotal: number;
    planUsd: number;
    planRiel: number;
    currentUsd: number;
    currentRiel: number;
    leftUsd: number;
    leftRiel: number;
  } | null>(null);
  const [budgetCapValue, setBudgetCapValue] = useState(BUDGET_CAP);
  const [editingSpendingId, setEditingSpendingId] = useState<number | null>(null);
  const [budgetDeleteId, setBudgetDeleteId] = useState<number | null>(null);
  const [isDeletingBudgetItem, setIsDeletingBudgetItem] = useState(false);
  const [isDeletingSelectedBudgetRows, setIsDeletingSelectedBudgetRows] = useState(false);
  const [isDeletingSelectedAttendanceRows, setIsDeletingSelectedAttendanceRows] = useState(false);
  const [budgetDeleteResult, setBudgetDeleteResult] = useState<string | null>(null);
  const [spending, setSpending] = useState<Spending[]>([]);
  const [budgetCurrencyOverrides, setBudgetCurrencyOverrides] = useState<
    Record<number, { budgetCurrency: BudgetCurrency; actualCurrency: BudgetCurrency }>
  >({});
  const [categoryTranslations, setCategoryTranslations] = useState<Record<string, string>>({});

  const clearAuthSession = useCallback(() => {
    clearStoredAuthToken();
    localStorage.removeItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY);
    setIsAuthed(false);
    setLoginUsername("");
    setLoginPassword("");
    setShowPassword(false);
    setAuthError("");
    setActiveTab("registry");
    setEditingRowId(null);
    setAttendanceViewStatus(null);
    setAttendanceDoneIds([]);
    setAttendanceDoneFilter("All");
    setAttendanceInviteFilter("All");
    setAttendanceGiftFilter("All");
    setAttendanceHouseholdFilter("all");
    setAttendanceCategoryFilter("all");
    setEditingSpendingId(null);
    setBudgetAmountModalOpen(false);
    setGiftGuestId(null);
    setGiftAmountInput("");
    setGiftCurrencyInput("Riel");
    setGiftInputError("");
    setSignOutConfirmOpen(false);
    setSessionExpiredModalOpen(false);
  }, []);

  const showSessionExpiredModal = useCallback(() => {
    clearStoredAuthToken();
    setSignOutConfirmOpen(false);
    setSessionExpiredModalOpen(true);
  }, []);

  const hasUnauthorizedStatus = (response: Response) =>
    response.status === 401 || response.status === 403;

  const handleUnauthorizedResponse = useCallback((response: Response) => {
    if (!hasUnauthorizedStatus(response)) {
      return false;
    }
    showSessionExpiredModal();
    return true;
  }, [showSessionExpiredModal]);

  const validateStoredAuthToken = useCallback(() => {
    if (authValidationRequestRef.current) {
      return authValidationRequestRef.current;
    }

    const validationRequest = (async () => {
      const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (!isStoredTokenValid(token)) {
        showSessionExpiredModal();
        return false;
      }

      try {
        const response = await fetch(buildApiUrl(API_AUTH_ME_PATH), {
          method: "GET",
          cache: "no-store",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401 || response.status === 403) {
          showSessionExpiredModal();
          return false;
        }

        // Protected API operations still perform their own authorization check.
        return response.ok || response.status >= 500;
      } catch {
        // Do not block local-only controls during a temporary network failure.
        return true;
      }
    })();

    authValidationRequestRef.current = validationRequest;
    void validationRequest.finally(() => {
      if (authValidationRequestRef.current === validationRequest) {
        authValidationRequestRef.current = null;
      }
    });
    return validationRequest;
  }, [showSessionExpiredModal]);

  const handleAuthenticatedInteractionCapture = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!isAuthed || sessionExpiredModalOpen || !(event.target instanceof Element)) {
        return;
      }

      if (event.target.closest('[data-auth-validation="skip"]')) {
        return;
      }

      const interactiveElement = event.target.closest(
        'button, a, input, select, textarea, label, [role="button"], [role="tab"]',
      );
      const replayTarget =
        interactiveElement instanceof HTMLElement
          ? interactiveElement
          : event.target instanceof HTMLElement
            ? event.target
            : event.target.parentElement;

      if (
        !replayTarget ||
        replayTarget.matches(":disabled")
      ) {
        return;
      }

      if (validatedInteractionReplayRef.current.has(replayTarget)) {
        validatedInteractionReplayRef.current.delete(replayTarget);
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      void validateStoredAuthToken().then((isValid) => {
        if (!isValid || !replayTarget.isConnected || replayTarget.matches(":disabled")) {
          return;
        }

        validatedInteractionReplayRef.current.add(replayTarget);
        replayTarget.click();
      });
    },
    [isAuthed, sessionExpiredModalOpen, validateStoredAuthToken],
  );

  const logoutWithToken = async (token: string) => {
    try {
      await fetch(buildApiUrl(API_AUTH_LOGOUT_PATH), {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        keepalive: true,
      });
    } catch {
      // Keep local sign-out successful even if logout API request fails.
    }
  };

  const handleBackToLogin = () => {
    const token = localStorage.getItem("wedding_access_token");
    clearAuthSession();
    if (token && isStoredTokenValid(token)) {
      void logoutWithToken(token);
    }
  };

  const attendance = useMemo(() => {
    const joining = attendanceGuests.filter((guest) => guest.status === "Joining");
    const notYet = attendanceGuests.filter((guest) => guest.status === "Not yet");
    const maybe = attendanceGuests.filter((guest) => guest.status === "Maybe");
    return { joining, notYet, maybe };
  }, [attendanceGuests]);

  const attendanceGuestsToView = useMemo(() => {
    if (attendanceViewStatus === "Joining") {
      return attendance.joining;
    }
    if (attendanceViewStatus === "Maybe") {
      return attendance.maybe;
    }
    if (attendanceViewStatus === "Not yet") {
      return attendance.notYet;
    }
    return [];
  }, [attendance, attendanceViewStatus]);

  const attendanceRows = useMemo(() => attendanceGuests, [attendanceGuests]);

  const attendanceGiftTotals = useMemo(() => {
    return attendanceRows.reduce(
      (totals, guest) => {
        if (guest.giftAmount == null) {
          return totals;
        }
        if (guest.giftCurrency === "Riel") {
          totals.riel += guest.giftAmount;
        } else {
          totals.usd += guest.giftAmount;
        }
        return totals;
      },
      { usd: 0, riel: 0 },
    );
  }, [attendanceRows]);

  const totals = useMemo(() => {
    const budgetTotal = spending.reduce((sum, item) => sum + item.budget, 0);
    const actualTotal = spending.reduce((sum, item) => sum + item.actual, 0);
    const planUsd = spending
      .filter((item) => item.budgetCurrency === "USD")
      .reduce((sum, item) => sum + item.budget, 0);
    const planRiel = spending
      .filter((item) => item.budgetCurrency === "Riel")
      .reduce((sum, item) => sum + item.budget, 0);
    const currentUsd = spending
      .filter((item) => item.actualCurrency === "USD")
      .reduce((sum, item) => sum + item.actual, 0);
    const currentRiel = spending
      .filter((item) => item.actualCurrency === "Riel")
      .reduce((sum, item) => sum + item.actual, 0);
    return {
      budgetTotal,
      actualTotal,
      planUsd,
      planRiel,
      currentUsd,
      currentRiel,
      leftUsd: planUsd - currentUsd,
      leftRiel: planRiel - currentRiel,
    };
  }, [spending]);

  const displayedTotals = budgetTotalsOverride ?? totals;
  const selectedRegistryGroup: GuestGroup =
    uploadedGroupFilter === "groom" ? "ភ្ញៀវកូនប្រុស" : "ភ្ញៀវកូនស្រី";
  const normalizeGuestName = (name: string) => name.trim().replace(/\s+/g, " ").toLowerCase();
  const duplicateUploadErrorMessage = useMemo(() => {
    if (duplicateUploadNames.length === 0) return "";
    const duplicateNames = duplicateUploadNames.slice(0, 3).join(", ");
    const hasMore = duplicateUploadNames.length > 3;
    return language === "kh"
      ? `ឈ្មោះនេះមានរួចហើយ: ${duplicateNames}${hasMore ? "..." : ""}. សូមប្តូរឈ្មោះមុនពេល Upload។`
      : `Duplicate name already exists: ${duplicateNames}${hasMore ? "..." : ""}. Please change the name before upload.`;
  }, [duplicateUploadNames, language]);
  const mapDraftToGuest = (item: RegistryDraftResponse): Guest => {
    const rawCategory = item.category ?? "";
    const categoryById = categoryItems.find(
      (entry) => String(entry.id) === String(rawCategory),
    );
    const normalizedGiftAmount =
      item.gift_amount == null ? null : toNumber(item.gift_amount);
    return {
      id: item.id,
      name: item.name ?? "",
      phone: "",
      status: "Not yet",
      group: GROUP_FROM_API[item.guest_group],
      category: categoryById?.name ?? rawCategory,
      notes: item.notes ?? "",
      latitude: item.latitude,
      longitude: item.longitude,
      source: "api",
      giftAmount: normalizedGiftAmount,
      giftCurrency: normalizeGiftCurrency(item.gift_currency),
      invited: Boolean(item.invited),
      done: Boolean(item.is_done),
      createdAt: item.created_at ?? null,
      updatedAt: item.updated_at ?? null,
    };
  };

  const mapApiGuestToGuest = (item: GuestsListItemResponse): Guest => {
    const normalizedGiftAmount =
      item.gift_amount == null ? null : toNumber(item.gift_amount);
    return {
      id: item.id,
      name: item.name ?? "",
      phone: "",
      status: "Not yet",
      group: GROUP_FROM_API[item.guest_group],
      category: item.category ?? "",
      notes: "",
      latitude: item.latitude,
      longitude: item.longitude,
      source: "api",
      giftAmount: normalizedGiftAmount,
      giftCurrency: normalizeGiftCurrency(item.gift_currency),
      invited: Boolean(item.invited),
      done: Boolean(item.done ?? item.is_done),
    };
  };

  const buildAttendanceParams = (
    page = 1,
    groupOverride?: "all" | "bride" | "groom",
  ) => {
    const params = new URLSearchParams({
      group:
        groupOverride ??
        (attendanceGroupFilter === "All"
          ? "all"
          : GROUP_TO_API[attendanceGroupFilter]),
      status:
        attendanceFilter === "All"
          ? "all"
          : STATUS_TO_API[attendanceFilter],
      done:
        attendanceDoneFilter === "All"
          ? "all"
          : attendanceDoneFilter === "Done"
            ? "done"
            : "pending",
      invite:
        attendanceInviteFilter === "All"
          ? "all"
          : attendanceInviteFilter === "Invited"
            ? "invited"
            : "not_invited",
      gift:
        attendanceGiftFilter === "All"
          ? "all"
          : attendanceGiftFilter === "Gift"
            ? "with_gift"
            : "no_gift",
      household: attendanceHouseholdFilter,
      category: attendanceCategoryFilter,
      page: String(page),
      limit: String(ATTENDANCE_TABLE_LIMIT),
    });

    if (attendanceInviteFilter === "Invited") {
      params.set("invited", "true");
    } else if (attendanceInviteFilter === "Not invited") {
      params.set("invited", "false");
    }

    if (attendanceGiftFilter === "Gift") {
      params.set("has_gift", "true");
    } else if (attendanceGiftFilter === "No gift") {
      params.set("has_gift", "false");
    }

    const query = attendanceSearch.trim();
    if (query) {
      params.set("q", query);
      params.set("query", query);
    }
    return params;
  };

  const loadAttendanceGuests = async () => {
    const requestId = ++attendanceGuestsRequestIdRef.current;
    setIsLoadingAttendanceGuests(true);
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      if (requestId === attendanceGuestsRequestIdRef.current) {
        setAttendanceGuests([]);
        setAttendanceDoneIds([]);
        setAttendanceTotalPages(1);
        setIsLoadingAttendanceGuests(false);
      }
      return;
    }

    try {
      const fetchAttendancePage = async (targetPage: number) => {
        const params = buildAttendanceParams(targetPage, "all");
        const response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}?${params.toString()}`, {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (handleUnauthorizedResponse(response)) {
          return null;
        }

        if (!response.ok) {
          throw new Error("Unable to load attendance guests.");
        }

        return (await response.json()) as GuestsListResponse;
      };

      const payload = await fetchAttendancePage(1);
      if (!payload) {
        return;
      }
      if (requestId !== attendanceGuestsRequestIdRef.current) {
        return;
      }

      const effectiveLimit = toNumber(payload.limit) || ATTENDANCE_TABLE_LIMIT;
      const serverTotalPages =
        typeof payload.total_pages === "number" && payload.total_pages > 0
          ? payload.total_pages
          : Math.max(1, Math.ceil(toNumber(payload.total) / effectiveLimit));

      let items = payload.items ?? [];
      if (serverTotalPages > 1) {
        const collected = [...items];
        for (let nextPage = 2; nextPage <= serverTotalPages; nextPage += 1) {
          const nextPayload = await fetchAttendancePage(nextPage);
          if (!nextPayload) {
            return;
          }
          if (requestId !== attendanceGuestsRequestIdRef.current) {
            return;
          }
          collected.push(...(nextPayload.items ?? []));
        }
        items = collected;
      }

      const mapped = items.map(mapApiGuestToGuest);
      const totalPages = Math.max(1, Math.ceil(mapped.length / ATTENDANCE_TABLE_LIMIT));

      setAttendanceGuests(mapped);
      setAttendanceDoneIds(
        mapped.filter((guest) => guest.done).map((guest) => guest.id),
      );
      setAttendanceTotalPages(totalPages);
      setAttendanceCounts({
        total: mapped.length,
        invited_count: mapped.filter((guest) => Boolean(guest.invited)).length,
        with_gift_count: mapped.filter((guest) => (guest.giftAmount ?? 0) > 0).length,
      });
      setAttendanceGroupCounts({
        all: mapped.length,
        bride: mapped.filter((guest) => guest.group === "ភ្ញៀវកូនស្រី").length,
        groom: mapped.filter((guest) => guest.group === "ភ្ញៀវកូនប្រុស").length,
      });
    } catch {
      if (requestId === attendanceGuestsRequestIdRef.current) {
        setAttendanceGuests([]);
        setAttendanceDoneIds([]);
        setAttendanceCounts({ total: 0, invited_count: 0, with_gift_count: 0 });
        setAttendanceGroupCounts({ all: 0, bride: 0, groom: 0 });
        setAttendanceTotalPages(1);
      }
    } finally {
      if (requestId === attendanceGuestsRequestIdRef.current) {
        setIsLoadingAttendanceGuests(false);
      }
    }
  };

  const handleAttendanceSearchChange = (value: string) => {
    setAttendancePage(1);
    setAttendanceSearch(value);
  };
  const setAttendanceGroupFilterWithPageReset: Dispatch<SetStateAction<AttendanceGroupFilter>> = (
    value,
  ) => {
    setAttendancePage(1);
    setAttendanceGroupFilter(value);
  };
  const setAttendanceDoneFilterWithPageReset: Dispatch<SetStateAction<AttendanceDoneFilter>> = (
    value,
  ) => {
    setAttendancePage(1);
    setAttendanceDoneFilter(value);
  };
  const setAttendanceInviteFilterWithPageReset: Dispatch<SetStateAction<AttendanceInviteFilter>> = (
    value,
  ) => {
    setAttendancePage(1);
    setAttendanceInviteFilter(value);
  };
  const setAttendanceGiftFilterWithPageReset: Dispatch<SetStateAction<AttendanceGiftFilter>> = (
    value,
  ) => {
    setAttendancePage(1);
    setAttendanceGiftFilter(value);
  };
  const setAttendanceHouseholdFilterWithPageReset: Dispatch<SetStateAction<AttendanceHouseholdFilter>> = (
    value,
  ) => {
    setAttendancePage(1);
    setAttendanceHouseholdFilter(value);
  };
  const setAttendanceCategoryFilterWithPageReset: Dispatch<SetStateAction<AttendanceCategoryFilter>> = (
    value,
  ) => {
    setAttendancePage(1);
    setAttendanceCategoryFilter(value);
  };

  const loadGuestEstimate = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setGuestEstimate(null);
      setGuestEstimateError(systemText[language].signInAgainLoadRecords);
      return;
    }

    setIsLoadingGuestEstimate(true);
    setGuestEstimateError("");
    try {
      const params = buildAttendanceParams();
      params.delete("page");
      params.delete("limit");
      params.delete("household");
      const response = await fetch(
        `${buildApiUrl(API_GUESTS_ESTIMATE_PATH)}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (handleUnauthorizedResponse(response)) {
        return;
      }

      if (!response.ok) {
        setGuestEstimate(null);
        setGuestEstimateError(
          language === "kh" ? "មិនអាចទាញយកការប៉ាន់ស្មានភ្ញៀវបានទេ។" : "Unable to load guest estimate.",
        );
        return;
      }

      const payload = (await response.json()) as GuestEstimateResponse;
      setGuestEstimate({
        totalFound: toNumber(payload.total_found),
        brideTotal: toNumber(payload.bride_total),
        groomTotal: toNumber(payload.groom_total),
        estimatedGuestTotal: toNumber(payload.estimated_guest_total),
        guestsPerTable: toNumber(payload.guests_per_table),
        estimatedTableCount: toNumber(payload.estimated_table_count),
      });
    } catch {
      setGuestEstimate(null);
      setGuestEstimateError(
        language === "kh" ? "មិនអាចទាញយកការប៉ាន់ស្មានភ្ញៀវបានទេ។" : "Unable to load guest estimate.",
      );
    } finally {
      setIsLoadingGuestEstimate(false);
    }
  };

  const patchAttendanceGuest = async (
    id: number,
    changes: Partial<Pick<Guest, "status" | "invited" | "done" | "giftAmount" | "giftCurrency">>,
  ) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      return;
    }

    const current = attendanceGuests.find((guest) => guest.id === id);
    if (!current) {
      return;
    }

    const merged: Guest = { ...current, ...changes };
    const categoryMatch = categoryItems.find((item) => item.name === merged.category);
    const payload = {
      name: merged.name,
      category: merged.category,
      category_id: categoryMatch?.id,
      guest_group: GROUP_TO_API[merged.group],
      latitude: merged.latitude ?? 0,
      longitude: merged.longitude ?? 0,
      invited: Boolean(merged.invited),
      done: Boolean(merged.done),
      gift_amount: merged.giftAmount ?? null,
      gift_currency:
        merged.giftAmount == null ? null : toApiGiftCurrency(merged.giftCurrency),
    };

    try {
      let response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}/${id}`, {
        method: "PATCH",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (
        !response.ok &&
        merged.giftAmount != null &&
        normalizeGiftCurrency(merged.giftCurrency) === "Riel"
      ) {
        response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}/${id}`, {
          method: "PATCH",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...payload,
            gift_currency: "riel",
          }),
        });
      }

      if (handleUnauthorizedResponse(response)) {
        return;
      }

      if (!response.ok) {
        return;
      }

      const updated = (await response.json()) as GuestsListItemResponse;
      const mapped = mapApiGuestToGuest(updated);
      setAttendanceGuests((prev) =>
        prev.map((guest) => (guest.id === id ? { ...guest, ...mapped } : guest)),
      );
      setAttendanceDoneIds((prev) => {
        const has = prev.includes(id);
        if (mapped.done) {
          return has ? prev : [...prev, id];
        }
        return has ? prev.filter((guestId) => guestId !== id) : prev;
      });
    } catch {
      // Keep current UI state when patch fails.
    }
  };

  const toNumber = (value: string | number | null | undefined) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === "string") {
      const parsed = Number(value.replace(/,/g, ""));
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const loadCategories = useCallback(async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      return;
    }
    try {
      const response = await fetch(buildApiUrl(API_CATEGORIES_PATH), {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (handleUnauthorizedResponse(response)) return;
      if (!response.ok) return;
      const payload = (await response.json()) as CategoryItem[];
      const normalized = payload
        .map((item) => ({ ...item, name: item.name?.trim() }))
        .filter((item) => Boolean(item.name));
      if (normalized.length > 0) {
        setCategoryItems(normalized as CategoryItem[]);
      }
    } catch {
      // Keep fallback category list if API is unavailable.
    }
  }, [handleUnauthorizedResponse]);

  const createCategory = async (nameEn: string, nameKh?: string) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      throw new Error(systemText[language].signInAgainLoadRecords);
    }
    const response = await fetch(buildApiUrl(API_CATEGORIES_PATH), {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name: nameEn }),
    });
    if (handleUnauthorizedResponse(response)) {
      throw new Error(systemText[language].signInAgainLoadRecords);
    }
    const payload = (await response.json().catch(() => null)) as CategoryItem | { message?: string } | null;
    if (!response.ok) {
      throw new Error(
        payload && "message" in payload ? payload.message ?? "Unable to create category." : "Unable to create category.",
      );
    }
    if (payload && "id" in payload && payload.name?.trim()) {
      const created = { ...payload, name: payload.name.trim() };
      setCategoryItems((prev) => {
        const exists = prev.some((item) => item.id === created.id);
        return exists ? prev : [...prev, created];
      });
    }
    if (nameKh?.trim()) {
      const key = nameEn.trim().toLowerCase();
      setCategoryTranslations((prev) => {
        const next = { ...prev, [key]: nameKh.trim() };
        localStorage.setItem(CATEGORY_TRANSLATIONS_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }
    await loadCategories();
  };

  const updateCategory = async (id: number, nameEn: string, nameKh?: string) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      throw new Error(systemText[language].signInAgainLoadRecords);
    }
    const previousName = categoryItems.find((item) => item.id === id)?.name ?? "";
    const response = await fetch(`${buildApiUrl(API_CATEGORIES_PATH)}/${id}`, {
      method: "PATCH",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name: nameEn }),
    });
    if (handleUnauthorizedResponse(response)) {
      throw new Error(systemText[language].signInAgainLoadRecords);
    }
    const payload = (await response.json().catch(() => null)) as CategoryItem | { message?: string } | null;
    if (!response.ok) {
      throw new Error(
        payload && "message" in payload ? payload.message ?? "Unable to update category." : "Unable to update category.",
      );
    }
    if (payload && "id" in payload && payload.name?.trim()) {
      const updated = { ...payload, name: payload.name.trim() };
      setCategoryItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } else {
      setCategoryItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, name: nameEn.trim() } : item)),
      );
    }
    if (previousName && previousName !== nameEn.trim()) {
      setGuests((prev) =>
        prev.map((guest) =>
          guest.category === previousName ? { ...guest, category: nameEn.trim() } : guest,
        ),
      );
      setAttendanceGuests((prev) =>
        prev.map((guest) =>
          guest.category === previousName ? { ...guest, category: nameEn.trim() } : guest,
        ),
      );
      setUploadedGuests((prev) =>
        prev.map((guest) =>
          guest.category === previousName ? { ...guest, category: nameEn.trim() } : guest,
        ),
      );
      const previousFilterKey = previousName.trim().toLowerCase();
      if (attendanceCategoryFilter.trim().toLowerCase() === previousFilterKey) {
        setAttendanceCategoryFilter(nameEn.trim());
      }
    }
    const oldKey = previousName.trim().toLowerCase();
    const newKey = nameEn.trim().toLowerCase();
    setCategoryTranslations((prev) => {
      const next = { ...prev };
      if (oldKey && oldKey !== newKey) {
        delete next[oldKey];
      }
      if (nameKh?.trim()) {
        next[newKey] = nameKh.trim();
      } else if (oldKey && oldKey !== newKey && prev[oldKey]) {
        next[newKey] = prev[oldKey];
      }
      localStorage.setItem(CATEGORY_TRANSLATIONS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    await loadCategories();
  };

  const deleteCategory = async (id: number) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      throw new Error(systemText[language].signInAgainLoadRecords);
    }
    const response = await fetch(`${buildApiUrl(API_CATEGORIES_PATH)}/${id}`, {
      method: "DELETE",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (handleUnauthorizedResponse(response)) {
      throw new Error(systemText[language].signInAgainLoadRecords);
    }
    const payload = (await response.json().catch(() => null)) as { message?: string } | null;
    if (!response.ok) {
      throw new Error(payload?.message ?? "Unable to delete category.");
    }
    const deletedName = categoryItems.find((item) => item.id === id)?.name ?? "";
    setCategoryItems((prev) => prev.filter((item) => item.id !== id));
    if (deletedName) {
      const deletedKey = deletedName.trim().toLowerCase();
      if (attendanceCategoryFilter.trim().toLowerCase() === deletedKey) {
        setAttendanceCategoryFilter("all");
      }
    }
    if (deletedName) {
      const key = deletedName.trim().toLowerCase();
      setCategoryTranslations((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        localStorage.setItem(CATEGORY_TRANSLATIONS_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }
    await loadCategories();
  };

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const username = loginUsername.trim();
    const password = loginPassword.trim();

    if (!username || !password) {
      setAuthNotice("");
      setAuthError(loginText[language].enterCredentials);
      return;
    }

    setIsLoggingIn(true);
    setAuthError("");
    setAuthNotice("");

    const persistLogin = (payload: LoginResponse) => {
      if (!payload.access_token) {
        return;
      }
      const expiresIn =
        typeof payload.expires_in === "number" && payload.expires_in > 0
          ? payload.expires_in
          : null;
      const jwtPayload = decodeJwtPayload(payload.access_token);
      const jwtExpiresAt =
        typeof jwtPayload?.exp === "number" ? jwtPayload.exp * 1000 : 0;
      const apiExpiresAt = expiresIn ? Date.now() + expiresIn * 1000 : 0;
      const validExpirations = [jwtExpiresAt, apiExpiresAt].filter(
        (value) => Number.isFinite(value) && value > 0,
      );
      const expiresAt =
        validExpirations.length > 0 ? Math.min(...validExpirations) : 0;

      if (expiresAt <= Date.now()) {
        clearStoredAuthToken();
        setAuthError(loginText[language].authError);
        return;
      }

      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, payload.access_token);
      if (payload.token_type) {
        localStorage.setItem(AUTH_TOKEN_TYPE_STORAGE_KEY, payload.token_type);
      }
      if (expiresIn) {
        localStorage.setItem(AUTH_TOKEN_EXPIRES_IN_STORAGE_KEY, String(expiresIn));
      }
      localStorage.setItem(AUTH_TOKEN_EXPIRES_AT_STORAGE_KEY, String(expiresAt));
      setIsAuthed(true);
      setAuthError("");
      setLoginUsername("");
      setLoginPassword("");
      setShowPassword(false);
    };

    try {
      const response = await fetch(buildApiUrl(API_AUTH_LOGIN_PATH), {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      let payload: LoginResponse | null = null;
      try {
        payload = (await response.json()) as LoginResponse;
      } catch {}

      if (!response.ok || !payload?.success || !payload.access_token) {
        setAuthError(payload?.message || loginText[language].authError);
        return;
      }

      persistLogin(payload);
    } catch {
      setAuthError(
        language === "kh"
          ? "មិនអាចភ្ជាប់ទៅម៉ាស៊ីនមេបានទេ។ សូមពិនិត្យការកំណត់ API ។"
          : "Unable to reach the API server. Check the API configuration.",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleRequestProjectAccess = () => {
    setAuthError("");
    setAuthNotice("");
    setIsAccessRequestPageOpen(true);
  };

  const openPaymentPopup = () => {
    if (!projectFullName.trim() || !projectContact.trim()) {
      setProjectRequiredNotice(
        language === "kh"
          ? "សូមបញ្ចូលឈ្មោះ និងទំនាក់ទំនង។"
          : "Please enter your name and contact.",
      );
      return;
    }

    setProjectRequiredNotice("");
    setPaymentPopupOpen(true);
    setPaymentPopupTab("scan");
    setPaymentScreenshotName("");
    setPaymentScreenshotFile(null);
    setPaymentSendNotice("");
  };

  const closePaymentPopup = () => {
    setPaymentPopupOpen(false);
    setPaymentPopupTab("scan");
    setPaymentScreenshotName("");
    setPaymentScreenshotFile(null);
    setPaymentSendNotice("");
    setIsSendingPaymentScreenshot(false);
  };

  const handlePaymentScreenshotUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setPaymentScreenshotFile(file);
    setPaymentScreenshotName(file?.name ?? "");
    setPaymentSendNotice("");
  };

  const sendPaymentScreenshotToOwner = async () => {
    const fullName = projectFullName.trim();
    const contact = projectContact.trim();

    if (!fullName || !contact) {
      setPaymentSendNotice(
        language === "kh"
          ? "សូមបញ្ចូលឈ្មោះ និងទំនាក់ទំនង។"
          : "Please enter your name and contact.",
      );
      return;
    }

    if (!paymentScreenshotFile) {
      setPaymentSendNotice(
        language === "kh"
          ? "សូមជ្រើសរើសរូបថតបង់ប្រាក់។"
          : "Please choose a payment screenshot.",
      );
      return;
    }

    const payload = new FormData();
    payload.set("fullName", fullName);
    payload.set("contact", contact);
    payload.set("screenshot", paymentScreenshotFile);

    setIsSendingPaymentScreenshot(true);
    setPaymentSendNotice("");

    try {
      const response = await fetch("/api/wedding/telegram/payment", {
        method: "POST",
        body: payload,
      });
      const result = (await response.json().catch(() => null)) as
        | { success?: boolean; message?: string; detail?: string }
        | null;

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || result?.detail || "Unable to send payment screenshot.");
      }

      setPaymentSendNotice(
        language === "kh"
          ? "បានផ្ញើទៅក្រុមការងារ Telegram រួចរាល់។"
          : "Sent to our team on Telegram.",
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to send payment screenshot.";
      setPaymentSendNotice(
        language === "kh"
          ? "មិនអាចផ្ញើដោយស្វ័យប្រវត្តិបានទេ។ សូមពិនិត្យការកំណត់ Telegram bot។"
          : message,
      );
    } finally {
      setIsSendingPaymentScreenshot(false);
    }
  };

  const handleSampleAccessRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fullName = sampleFullName.trim();
    const contact = sampleContact.trim();
    if (!fullName || !contact) {
      setSampleRequestNotice(
        language === "kh"
          ? "សូមបញ្ចូលឈ្មោះ និងទំនាក់ទំនង។"
          : "Please enter full name and contact.",
      );
      return;
    }

    const message = [
      "Wedding Controller sample access request",
      `Full name: ${fullName}`,
      `Contact: ${contact}`,
      "Requested sample credentials: admin / admin1234",
    ].join("\n");
    localStorage.setItem(
      "wedding_sample_access_request",
      JSON.stringify({ fullName, contact, createdAt: new Date().toISOString() }),
    );

    setIsSendingSampleRequest(true);
    setSampleRequestNotice("");

    try {
      const response = await fetch("/api/wedding/telegram/message", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ fullName, contact, message }),
      });
      const result = (await response.json().catch(() => null)) as
        | { success?: boolean; message?: string; detail?: string }
        | null;

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || result?.detail || "Unable to send Telegram message.");
      }

      setSampleCredentialsUnlocked(true);
      setSampleRequestNotice(
        language === "kh"
          ? "បានផ្ញើទៅក្រុមការងារ Telegram រួចរាល់។"
          : "Sent to our team on Telegram.",
      );
    } catch (error) {
      const fallbackMessage =
        error instanceof Error ? error.message : "Unable to send Telegram message.";
      setSampleRequestNotice(
        language === "kh"
          ? "មិនអាចផ្ញើទៅ Telegram បានទេ។ សូមពិនិត្យការកំណត់ bot។"
          : fallbackMessage,
      );
    } finally {
      setIsSendingSampleRequest(false);
    }
  };

  useEffect(() => {
    if (!isAuthed) return;
    void loadCategories();
  }, [isAuthed, loadCategories]);

  const updateGuest = (
    id: number,
    changes: Partial<
      Pick<
        Guest,
        | "name"
        | "phone"
        | "status"
        | "group"
        | "category"
        | "notes"
        | "latitude"
        | "longitude"
        | "giftAmount"
        | "giftCurrency"
        | "invited"
      >
    >,
  ) => {
    if (typeof changes.category === "string" && changes.category.trim()) {
      setLastSelectedRegistryCategory(changes.category);
    }
    setGuests((prev) =>
      prev.map((guest) => (guest.id === id ? { ...guest, ...changes } : guest)),
    );
  };

  const createGuestId = () => {
    guestIdSeedRef.current += 1;
    return guestIdSeedRef.current;
  };

  const addGuestRow = () => {
    setEditingRowId(null);
    setRegistrySearch("");
    setGuests((prev) => {
      const nextCategory =
        (lastSelectedRegistryCategory.trim() &&
        categories.includes(lastSelectedRegistryCategory)
          ? lastSelectedRegistryCategory
          : categories[0]) ?? "";
      const newGuest: Guest = {
        id: createGuestId(),
        name: "",
        phone: "",
        status: "Joining",
        group: selectedRegistryGroup,
        category: nextCategory,
        notes: "",
        latitude: null,
        longitude: null,
        source: "local" as const,
        giftAmount: null,
        giftCurrency: "USD" as const,
        invited: false,
      };

      if (prev.length === 0) {
        return [newGuest];
      }

      return [prev[0], newGuest, ...prev.slice(1)];
    });
  };

  const removeGuestRow = (id: number) => {
    setGuests((prev) => prev.filter((guest) => guest.id !== id));
    setNameErrorIds((prev) => prev.filter((errorId) => errorId !== id));
    if (editingRowId === id) {
      setEditingRowId(null);
    }
    if (mapGuestId === id) {
      setMapGuestId(null);
      setMapOpen(false);
    }
  };

  const removeGuestRows = (ids: number[]) => {
    if (ids.length === 0) {
      return;
    }
    const idSet = new Set(ids);
    setGuests((prev) => prev.filter((guest) => !idSet.has(guest.id)));
    setNameErrorIds((prev) => prev.filter((errorId) => !idSet.has(errorId)));
    if (editingRowId != null && idSet.has(editingRowId)) {
      setEditingRowId(null);
    }
    if (mapGuestId != null && idSet.has(mapGuestId)) {
      setMapGuestId(null);
      setMapOpen(false);
    }
  };

  const handleUploadSnapshot = async () => {
    if (registryGuests.length === 0) {
      return;
    }

    const missingNameIds = registryGuests
      .filter((guest) => !guest.name.trim())
      .map((guest) => guest.id);
    if (missingNameIds.length > 0) {
      setNameErrorIds((prev) => [...new Set([...prev, ...missingNameIds])]);
      return;
    }

    setNameErrorIds((prev) =>
      prev.filter((id) => !registryGuests.some((guest) => guest.id === id)),
    );
    setUploadError("");
    setDuplicateUploadNames([]);

    const duplicateNameLabels = new Set<string>();

    const localNameCount = new Map<string, number>();
    const localNameLabel = new Map<string, string>();
    for (const guest of registryGuests) {
      const trimmedName = guest.name.trim();
      const normalizedName = normalizeGuestName(trimmedName);
      localNameCount.set(normalizedName, (localNameCount.get(normalizedName) ?? 0) + 1);
      if (!localNameLabel.has(normalizedName)) {
        localNameLabel.set(normalizedName, trimmedName);
      }
    }
    for (const [normalizedName, count] of localNameCount.entries()) {
      if (count > 1) {
        duplicateNameLabels.add(localNameLabel.get(normalizedName) ?? normalizedName);
      }
    }

    const apiNameToIds = new Map<string, Set<number>>();
    for (const guest of guests) {
      if ((guest.source ?? "local") !== "api") continue;
      const normalizedName = normalizeGuestName(guest.name);
      if (!normalizedName) continue;
      const ids = apiNameToIds.get(normalizedName) ?? new Set<number>();
      ids.add(guest.id);
      apiNameToIds.set(normalizedName, ids);
    }

    for (const guest of registryGuests) {
      const normalizedName = normalizeGuestName(guest.name);
      const existingIds = apiNameToIds.get(normalizedName);
      if (!existingIds || existingIds.size === 0) continue;
      const matchedIds = Array.from(existingIds);
      const isSameUploadedRowOnly =
        guest.uploadedId != null &&
        matchedIds.length === 1 &&
        matchedIds[0] === guest.uploadedId;
      if (!isSameUploadedRowOnly) {
        duplicateNameLabels.add(guest.name.trim());
      }
    }

    if (duplicateNameLabels.size > 0) {
      setDuplicateUploadNames(Array.from(duplicateNameLabels));
      return;
    }

    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setDuplicateUploadNames([]);
      setUploadError(systemText[language].signInAgainUpload);
      return;
    }

    setIsUploadingDrafts(true);

    try {
      const draftGuests = registryGuests.filter((guest) => guest.uploadedId == null);
      const uploadedGuestsToUpdate = registryGuests.filter(
        (guest): guest is Guest & { uploadedId: number } => guest.uploadedId != null,
      );

      await Promise.all(
        draftGuests.map(async (guest) => {
          const category = categoryItems.find((item) => item.name === guest.category);
          const payload = {
            guest_group: GROUP_TO_API[guest.group],
            name: guest.name.trim(),
            category: guest.category,
            category_id: category?.id,
            notes: guest.notes || "",
            latitude: guest.latitude ?? 0,
            longitude: guest.longitude ?? 0,
          };

          const response = await fetch(buildApiUrl(API_REGISTRY_DRAFTS_PATH), {
            method: "POST",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          if (handleUnauthorizedResponse(response)) {
            throw new Error(systemText[language].signInAgainUpload);
          }

          let result: RegistryDraftResponse | { message?: string } | null = null;
          try {
            result = (await response.json()) as RegistryDraftResponse | { message?: string };
          } catch {}

          if (!response.ok) {
            throw new Error(result && "message" in result ? result.message : "Upload failed.");
          }
        }),
      );

      await Promise.all(
        uploadedGuestsToUpdate.map(async (guest) => {
          const category = categoryItems.find((item) => item.name === guest.category);
          const payload = {
            name: guest.name.trim(),
            category: guest.category,
            category_id: category?.id,
            guest_group: GROUP_TO_API[guest.group],
            latitude: guest.latitude ?? 0,
            longitude: guest.longitude ?? 0,
            invited: Boolean(guest.invited),
            done: Boolean(guest.done),
            gift_amount: guest.giftAmount ?? null,
            gift_currency:
              guest.giftAmount == null ? null : toApiGiftCurrency(guest.giftCurrency),
          };

          let response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}/${guest.uploadedId}`, {
            method: "PATCH",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          if (
            !response.ok &&
            guest.giftAmount != null &&
            normalizeGiftCurrency(guest.giftCurrency) === "Riel"
          ) {
            response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}/${guest.uploadedId}`, {
              method: "PATCH",
              headers: {
                accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                ...payload,
                gift_currency: "riel",
              }),
            });
          }

          if (handleUnauthorizedResponse(response)) {
            throw new Error(systemText[language].signInAgainUpload);
          }

          let result: GuestsListItemResponse | { message?: string } | null = null;
          try {
            result = (await response.json()) as GuestsListItemResponse | { message?: string };
          } catch {}

          if (!response.ok) {
            throw new Error(result && "message" in result ? result.message : "Upload failed.");
          }
        }),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : systemText[language].unableUploadRegistryDrafts;
      setDuplicateUploadNames([]);
      setUploadError(message);
      setRegistryUpdateResult({
        type: "error",
        message: systemText[language].failedUpdate,
      });
      setIsUploadingDrafts(false);
      return;
    }

    const uploadedLocalIds = new Set(registryGuests.map((guest) => guest.id));
    const batch: UploadedBatch = {
      id: Date.now(),
      group: selectedRegistryGroup,
      createdAt: new Date().toLocaleString(),
      records: registryGuests.map((guest) => ({ ...guest })),
    };
    setUploadedBatches((prev) => [batch, ...prev]);
    setGuests((prev) => prev.filter((guest) => !uploadedLocalIds.has(guest.id)));
    setNameErrorIds([]);
    setDuplicateUploadNames([]);
    setRegistryDataMode("drafts");
    setRegistrySearch("");
    setEditingRowId(null);
    setUploadedGroupFilter("all");
    setUploadedGuestsPage(1);
    setShowUploadedTable(true);
    await loadAllDrafts();
    await loadUploadedDrafts("all", 1);
    await loadAttendanceGuests();
    setUploadedDraftsReloadKey((prev) => prev + 1);
    setIsUploadingDrafts(false);
    setRegistryUpdateResult({
      type: "success",
      message: systemText[language].updatedSuccessfully,
    });
  };

  const handleEditUploadedRows = (uploadedRows: Guest[]) => {
    if (uploadedRows.length === 0) {
      return;
    }

    let firstEditableRowId: number | null = null;
    setGuests((prev) => {
      const existingUploadedIds = new Set(
        prev
          .map((guest) => guest.uploadedId)
          .filter((uploadedId): uploadedId is number => typeof uploadedId === "number"),
      );

      const editableRows: Guest[] = [];
      for (const guest of uploadedRows) {
        if (existingUploadedIds.has(guest.id)) {
          continue;
        }
        const editableRow: Guest = {
          ...guest,
          id: createGuestId(),
          uploadedId: guest.id,
          source: "local",
          invited: guest.invited ?? false,
        };
        editableRows.push(editableRow);
      }

      if (editableRows.length === 0) {
        return prev;
      }

      firstEditableRowId = editableRows[0].id;
      return [...editableRows, ...prev];
    });

    setEditingRowId(firstEditableRowId);
    setRegistrySearch("");
    setShowUploadedTable(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditUploadedRow = (guest: Guest) => {
    handleEditUploadedRows([guest]);
  };

  const deleteUploadedRowsByIds = async (ids: number[]) => {
    if (ids.length === 0) {
      return;
    }
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setUploadedGuestsError(systemText[language].signInAgainDeleteRecords);
      return;
    }

    setUploadedGuestsError("");
    const deletedIds: number[] = [];

    try {
      for (const id of ids) {
        const response = await fetch(`${buildApiUrl(API_REGISTRY_DRAFTS_PATH)}/${id}`, {
          method: "DELETE",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        let payload: { message?: string; detail?: string } | null = null;
        try {
          payload = (await response.json()) as { message?: string; detail?: string };
        } catch {}

        if (hasUnauthorizedStatus(response)) {
          showSessionExpiredModal();
          throw new Error(systemText[language].signInAgainDeleteRecords);
        }

        if (!response.ok) {
          throw new Error(payload?.detail || payload?.message || "Delete failed.");
        }

        deletedIds.push(id);
      }

      const deletedIdSet = new Set(deletedIds);
      if (registryDataMode === "same-name") {
        const remainingRows = registrySameNameRows.filter((guest) => !deletedIdSet.has(guest.id));
        setRegistrySameNameRows(remainingRows);
        const remainingRowsOnPage = uploadedGuests.filter((guest) => !deletedIdSet.has(guest.id)).length;
        const nextPage = remainingRowsOnPage === 0 && uploadedGuestsPage > 1 ? uploadedGuestsPage - 1 : uploadedGuestsPage;
        applyRegistrySameNamePage(remainingRows, nextPage);
      } else {
        setUploadedGuests((prev) => prev.filter((guest) => !deletedIdSet.has(guest.id)));
      }
      await loadAllDrafts();
    } catch (error) {
      setUploadedGuestsError(
        error instanceof Error ? error.message : systemText[language].unableDeleteUploadedRecord,
      );
    }
  };

  const handleDeleteUploadedRow = async () => {
    if (uploadedDeleteId == null) {
      return;
    }
    setIsDeletingDraft(true);
    await deleteUploadedRowsByIds([uploadedDeleteId]);
    setIsDeletingDraft(false);
    setUploadedDeleteId(null);
  };

  const handleDeleteSelectedUploadedRows = async (ids: number[]) => {
    if (ids.length === 0) {
      return;
    }
    setIsDeletingSelectedUploadedRows(true);
    await deleteUploadedRowsByIds(ids);
    setIsDeletingSelectedUploadedRows(false);
  };

  const handleDownloadRegistryExcel = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      return;
    }

    let savedGuests: Guest[] = [];

    if (registryDataMode === "same-name") {
      const payload = await fetchSameNameGuests(token, registrySameNameGiftFilter);
      if (!payload) {
        return;
      }
      savedGuests = (payload.items ?? []).map(mapApiGuestToGuest);
    } else {
      const normalizedQuery = registrySearch.trim();
      if (!normalizedQuery) {
        const items = await fetchAllWeddingGuestPages(token, uploadedGroupFilter);
        savedGuests = items.map(mapApiGuestToGuest);
      } else if (uploadedGroupFilter === "all") {
        const items = await fetchAllWeddingGuestPages(token, "all");
        const mapped = items.map(mapApiGuestToGuest);
        savedGuests = filterRegistryGuestsByQuery(mapped, normalizedQuery);
      } else {
        const items = await fetchAllWeddingGuestPages(token, uploadedGroupFilter);
        const mapped = items.map(mapApiGuestToGuest);
        savedGuests = filterRegistryGuestsByQuery(mapped, normalizedQuery);
      }
    }

    if (savedGuests.length === 0) {
      return;
    }
    downloadRegistryExcelFile({
      guests: savedGuests,
      language,
      tableText: registryText[language].table,
      selectedRegistryGroup,
    });
  };

  const buildGuestsExcel = (
    rowsToExport: Guest[],
    fileName: string,
    summaryRows: Array<{ label: string; value: string | number }> = [],
  ) => {
    const localizedGuestsText = guestsText[language];
    const usdFormatter = new Intl.NumberFormat(language === "kh" ? "km-KH" : "en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

    const escapeHtml = (value: string) =>
      value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");

    const rows = rowsToExport
      .map((guest, index) => {
        const groupLabel =
          guest.group === "ភ្ញៀវកូនស្រី"
            ? localizedGuestsText.groupFilter.bride
            : localizedGuestsText.groupFilter.groom;
        const inviteLabel = guest.invited
          ? localizedGuestsText.invitedFilter.Invited
          : localizedGuestsText.invitedFilter["Not invited"];
        const doneLabel = guest.done
          ? localizedGuestsText.doneFilter.Done
          : localizedGuestsText.doneFilter.Pending;
        const cells = [
          index + 1,
          guest.name || "",
          guest.phone || "",
          guest.category || "",
          localizedGuestsText.status[guest.status] || "",
          groupLabel,
          guest.latitude != null ? guest.latitude.toFixed(6) : "",
          guest.longitude != null ? guest.longitude.toFixed(6) : "",
          guest.giftAmount != null
            ? guest.giftCurrency === "Riel"
              ? `${new Intl.NumberFormat("km-KH").format(guest.giftAmount)} ៛`
              : usdFormatter.format(guest.giftAmount)
            : "",
          inviteLabel,
          doneLabel,
        ]
          .map((cell) => `<td>${escapeHtml(String(cell))}</td>`)
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    const summaryHtml =
      summaryRows.length === 0
        ? ""
        : `<table border="1" style="margin-bottom: 16px;">
    <thead>
      <tr>
        <th>Metric</th>
        <th>Value</th>
      </tr>
    </thead>
    <tbody>${summaryRows
      .map(
        (row) =>
          `<tr><td>${escapeHtml(String(row.label))}</td><td>${escapeHtml(String(row.value))}</td></tr>`,
      )
      .join("")}</tbody>
  </table>`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
</head>
<body>
  ${summaryHtml}
  <table border="1">
    <thead>
      <tr>
        <th>${localizedGuestsText.table.no}</th>
        <th>${localizedGuestsText.table.name}</th>
        <th>${localizedGuestsText.table.contact}</th>
        <th>${localizedGuestsText.table.category}</th>
        <th>${localizedGuestsText.table.status}</th>
        <th>${localizedGuestsText.table.group}</th>
        <th>${localizedGuestsText.table.latitude}</th>
        <th>${localizedGuestsText.table.longitude}</th>
        <th>${localizedGuestsText.table.gift}</th>
        <th>${localizedGuestsText.table.invite}</th>
        <th>${localizedGuestsText.table.action}</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const fetchAllAttendanceGuests = async (
    token: string,
    giftOverride: AttendanceGiftApiFilter | null = null,
  ) => {
    const fetchExportPage = async (page: number) => {
      const params = buildAttendanceParams(page);
      params.set("limit", String(ATTENDANCE_EXPORT_LIMIT));
      if (giftOverride) {
        params.set("gift", giftOverride);
      }

      const response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}?${params.toString()}`, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (handleUnauthorizedResponse(response)) {
        return null;
      }
      if (!response.ok) {
        throw new Error("Unable to load guest list.");
      }

      return (await response.json()) as GuestsListResponse;
    };

    const firstPagePayload = await fetchExportPage(1);
    if (!firstPagePayload) {
      return null;
    }

    const firstPageItems = (firstPagePayload.items ?? []).map(mapApiGuestToGuest);
    const effectiveLimit = toNumber(firstPagePayload.limit) || ATTENDANCE_EXPORT_LIMIT;
    const totalPages =
      typeof firstPagePayload.total_pages === "number" && firstPagePayload.total_pages > 0
        ? firstPagePayload.total_pages
        : Math.max(1, Math.ceil(toNumber(firstPagePayload.total) / effectiveLimit));

    if (totalPages <= 1) {
      return firstPageItems;
    }

    const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
    const orderedPayloads = new Map<number, GuestsListResponse>();

    for (let index = 0; index < remainingPages.length; index += ATTENDANCE_EXPORT_CONCURRENCY) {
      const batch = remainingPages.slice(index, index + ATTENDANCE_EXPORT_CONCURRENCY);
      const batchResults = await Promise.all(
        batch.map(async (page) => {
          const payload = await fetchExportPage(page);
          return payload ? ([page, payload] as const) : null;
        }),
      );

      for (const entry of batchResults) {
        if (!entry) {
          return null;
        }
        orderedPayloads.set(entry[0], entry[1]);
      }
    }

    const collected = [...firstPageItems];
    for (let page = 2; page <= totalPages; page += 1) {
      const payload = orderedPayloads.get(page);
      if (!payload) {
        continue;
      }
      collected.push(...(payload.items ?? []).map(mapApiGuestToGuest));
    }

    return collected;
  };

  const buildGuestSummaryRows = (rowsToExport: Guest[]) => {
    const brideCount = rowsToExport.filter((guest) => guest.group === "ភ្ញៀវកូនស្រី").length;
    const groomCount = rowsToExport.filter((guest) => guest.group === "ភ្ញៀវកូនប្រុស").length;
    const withGiftCount = rowsToExport.filter((guest) => (guest.giftAmount ?? 0) > 0).length;
    const totals = rowsToExport.reduce(
      (accumulator, guest) => {
        if ((guest.giftAmount ?? 0) <= 0) {
          return accumulator;
        }
        if (guest.giftCurrency === "Riel") {
          accumulator.riel += guest.giftAmount ?? 0;
        } else {
          accumulator.usd += guest.giftAmount ?? 0;
        }
        return accumulator;
      },
      { usd: 0, riel: 0 },
    );

    return [
      { label: language === "kh" ? "ភ្ញៀវសរុប" : "Total guests", value: rowsToExport.length },
      { label: language === "kh" ? "ភ្ញៀវខាងកូនស្រី" : "Bride guests", value: brideCount },
      { label: language === "kh" ? "ភ្ញៀវខាងកូនប្រុស" : "Groom guests", value: groomCount },
      { label: language === "kh" ? "ភ្ញៀវមានចំណងដៃ" : "Guests with gift", value: withGiftCount },
      { label: language === "kh" ? "ចំណងដៃសរុប USD" : "Total gift USD", value: formatCurrency(totals.usd) },
      {
        label: language === "kh" ? "ចំណងដៃសរុប Riel" : "Total gift Riel",
        value: `${new Intl.NumberFormat("km-KH").format(totals.riel)} ៛`,
      },
    ];
  };

  const handleDownloadAllGuestsExcel = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token || isDownloadingAllGuests) {
      return;
    }

    setIsDownloadingAllGuests(true);
    try {
      const collected = await fetchAllAttendanceGuests(token);
      if (!collected || collected.length === 0) {
        return;
      }

      buildGuestsExcel(collected, `guests-all-${new Date().toISOString().slice(0, 10)}.xls`);
    } finally {
      setIsDownloadingAllGuests(false);
    }
  };

  const loadGuestsWithGift = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token || isLoadingGuestsWithGift) {
      return [];
    }

    setIsLoadingGuestsWithGift(true);
    try {
      const collected = await fetchAllAttendanceGuests(token, "with_gift");
      return collected ?? [];
    } finally {
      setIsLoadingGuestsWithGift(false);
    }
  };

  const showAttendanceSameNameHaveGift = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token || isLoadingAttendanceGuests) {
      return;
    }

    setIsLoadingAttendanceGuests(true);
    try {
      const payload = await fetchSameNameGuests(token, "with_gift");
      if (!payload) {
        return;
      }

      const mapped = (payload.items ?? []).map(mapApiGuestToGuest);
      setAttendanceDataMode("same-name-have-gift");
      setAttendanceSameNameRows(mapped);
      applyAttendanceSameNamePage(mapped, 1);
    } finally {
      setIsLoadingAttendanceGuests(false);
    }
  };

  const showDefaultAttendanceGuests = () => {
    attendanceRequestSignatureRef.current = null;
    setAttendanceDataMode("default");
    setAttendanceSameNameRows([]);
    setAttendanceGuests([]);
    setAttendanceDoneIds([]);
    setAttendanceTotalPages(1);
    setAttendancePage(1);
  };

  const handleDownloadGuestsWithGiftExcel = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token || isDownloadingGuestsWithGift) {
      return;
    }

    setIsDownloadingGuestsWithGift(true);
    try {
      const collected = await fetchAllAttendanceGuests(token, "with_gift");
      if (!collected || collected.length === 0) {
        return;
      }

      buildGuestsExcel(
        collected,
        `guests-with-gift-${new Date().toISOString().slice(0, 10)}.xls`,
        buildGuestSummaryRows(collected),
      );
    } finally {
      setIsDownloadingGuestsWithGift(false);
    }
  };

  const handleDownloadGuestImportSample = () => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet([
      {
        guest_name: "Sok Dara",
        group_guest: "bride",
        gift_amount: 50,
        currency: "USD",
      },
      {
        guest_name: "Vannak Family",
        group_guest: "groom",
        gift_amount: "",
        currency: "",
      },
    ]);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Guests");
    XLSX.writeFile(workbook, "guest-upload-sample.xlsx");
  };

  const handlePreviewGuestExcel = async (file: File, mode: GuestImportMode) => {
    return parseGuestImportWorkbook(file, mode);
  };

  const handleUploadGuestExcel = async (file: File, mode: GuestImportMode) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      throw new Error(systemText[language].signInAgainUpload);
    }

    const parsedRows = await parseGuestImportWorkbook(file, mode);
    setIsUploadingGuestExcel(true);

    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(
        parsedRows.map((row) => ({
          guest_name: row.name,
          group_guest:
            row.group == null
              ? mode === "groom"
                ? "groom"
                : "bride"
              : GROUP_TO_API[row.group],
          gift_amount: row.hasGift ? row.giftAmount ?? "" : "",
          currency: row.hasGift ? row.giftCurrency : "",
        })),
      );
      XLSX.utils.book_append_sheet(workbook, worksheet, "Guests");
      const workbookArray = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const uploadFile = new File([workbookArray], file.name || "guest-upload.xlsx", {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const formData = new FormData();
      formData.append("file", uploadFile);

      const response = await fetch(buildApiUrl(API_GUEST_GIFTS_IMPORT_PATH), {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (handleUnauthorizedResponse(response)) {
        throw new Error(systemText[language].signInAgainUpload);
      }

      const payload = (await response.json().catch(() => null)) as GuestGiftImportResponse | { detail?: string } | null;
      if (!response.ok) {
        throw new Error(
          payload && "detail" in payload && payload.detail
            ? payload.detail
            : "Unable to upload guest file.",
        );
      }

      await loadAttendanceGuests();

      const importResult = payload as GuestGiftImportResponse;
      const firstError = importResult.errors[0]?.message;

      return {
        created: importResult.updated_count,
        failed: importResult.failed_count,
        message:
          importResult.failed_count > 0
            ? `${importResult.updated_count} uploaded, ${importResult.failed_count} failed. ${firstError ?? ""}`.trim()
            : `${importResult.updated_count} guests uploaded successfully.`,
      };
    } finally {
      setIsUploadingGuestExcel(false);
    }
  };

  const handleSpendingSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const budgetValue = toNumber(spendingForm.budget);
    const actualValue = toNumber(spendingForm.actual);

    if (!spendingForm.item.trim()) {
      return;
    }

    if (editingSpendingId == null) {
      void (async () => {
        const token = localStorage.getItem("wedding_access_token");
        if (!token) {
          return;
        }
        try {
          const response = await fetch(buildApiUrl(API_BUDGET_ITEMS_PATH), {
            method: "POST",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              item: spendingForm.item.trim(),
              plan_amount: budgetValue,
              plan_currency: spendingForm.budgetCurrency,
              current_amount: Number.isNaN(actualValue) ? 0 : actualValue,
              current_currency: spendingForm.actualCurrency,
              notes: spendingForm.note.trim() || "Planned",
            }),
          });

          if (handleUnauthorizedResponse(response)) {
            return;
          }

          if (!response.ok) {
            return;
          }

          const created = (await response.json().catch(() => null)) as BudgetItemResponse | null;
          if (created?.id != null) {
            setBudgetCurrencyOverrides((prev) => ({
              ...prev,
              [created.id]: {
                budgetCurrency: spendingForm.budgetCurrency,
                actualCurrency: spendingForm.actualCurrency,
              },
            }));
            setSpending((prev) => [
              mapBudgetItemToSpending(
                created,
                spendingForm.budgetCurrency,
                spendingForm.actualCurrency,
              ),
              ...prev.filter((item) => item.id !== created.id),
            ]);
          }

          setSpendingForm({
            item: "",
            budget: "",
            budgetCurrency: "USD",
            actual: "",
            actualCurrency: "USD",
            note: "",
          });
          await loadBudgetPage();
          await loadBudgetTotals();
        } catch {
          // Keep current form values if request fails.
        }
      })();
      return;
    }

    if (editingSpendingId != null) {
      void (async () => {
        const token = localStorage.getItem("wedding_access_token");
        if (!token) {
          return;
        }
        try {
          const response = await fetch(
            `${buildApiUrl(API_BUDGET_ITEMS_PATH)}/${editingSpendingId}`,
            {
              method: "PATCH",
              headers: {
                accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                item: spendingForm.item.trim(),
                plan_amount: budgetValue,
                plan_currency: spendingForm.budgetCurrency,
                current_amount: Number.isNaN(actualValue) ? 0 : actualValue,
                current_currency: spendingForm.actualCurrency,
                notes: spendingForm.note.trim() || "Planned",
              }),
            },
          );

          if (handleUnauthorizedResponse(response)) {
            return;
          }

          if (!response.ok) {
            return;
          }

          const updated = (await response.json().catch(() => null)) as BudgetItemResponse | null;
          const targetId = updated?.id ?? editingSpendingId;
          if (targetId != null) {
            setBudgetCurrencyOverrides((prev) => ({
              ...prev,
              [targetId]: {
                budgetCurrency: spendingForm.budgetCurrency,
                actualCurrency: spendingForm.actualCurrency,
              },
            }));
            if (updated) {
              setSpending((prev) =>
                prev.map((item) =>
                  item.id === targetId
                    ? mapBudgetItemToSpending(
                        updated,
                        spendingForm.budgetCurrency,
                        spendingForm.actualCurrency,
                      )
                    : item,
                ),
              );
            }
          }

          setEditingSpendingId(null);
          setSpendingForm({
            item: "",
            budget: "",
            budgetCurrency: "USD",
            actual: "",
            actualCurrency: "USD",
            note: "",
          });
          await loadBudgetPage();
          await loadBudgetTotals();
        } catch {
          // Keep form values if request fails.
        }
      })();
      return;
    }
  };

  const deleteSpendingRow = () => {
    if (budgetDeleteId == null) {
      return;
    }
    void (async () => {
      const token = localStorage.getItem("wedding_access_token");
      if (!token) {
        return;
      }
      try {
        setIsDeletingBudgetItem(true);
        const response = await fetch(`${buildApiUrl(API_BUDGET_ITEMS_PATH)}/${budgetDeleteId}`, {
          method: "DELETE",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (handleUnauthorizedResponse(response)) {
          return;
        }

        if (!response.ok) {
          return;
        }

        const deleted =
          (await response.json().catch(() => null)) as
            | { success?: boolean; message?: string; id?: number }
            | null;
        const deletedId = deleted?.id ?? budgetDeleteId;

        if (editingSpendingId === deletedId) {
          setEditingSpendingId(null);
          setSpendingForm({
            item: "",
            budget: "",
            budgetCurrency: "USD",
            actual: "",
            actualCurrency: "USD",
            note: "",
          });
        }

        setSpending((prev) => prev.filter((item) => item.id !== deletedId));

        setBudgetCurrencyOverrides((prev) => {
          if (!(deletedId in prev)) return prev;
          const next = { ...prev };
          delete next[deletedId];
          return next;
        });

        await loadBudgetPage();
        await loadBudgetTotals();
        setBudgetDeleteId(null);
        setBudgetDeleteResult(systemText[language].deletedSuccessfully);
      } catch {
        // Keep current UI state if delete fails.
      } finally {
        setIsDeletingBudgetItem(false);
      }
    })();
  };

  const deleteSelectedSpendingRows = async (ids: number[]) => {
    if (ids.length === 0) {
      return;
    }
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      return;
    }

    setIsDeletingSelectedBudgetRows(true);
    try {
      for (const id of ids) {
        const response = await fetch(`${buildApiUrl(API_BUDGET_ITEMS_PATH)}/${id}`, {
          method: "DELETE",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (handleUnauthorizedResponse(response)) {
          return;
        }
      }

      if (editingSpendingId != null && ids.includes(editingSpendingId)) {
        setEditingSpendingId(null);
        setSpendingForm({
          item: "",
          budget: "",
          budgetCurrency: "USD",
          actual: "",
          actualCurrency: "USD",
          note: "",
        });
      }

      setBudgetCurrencyOverrides((prev) => {
        const next = { ...prev };
        let changed = false;
        ids.forEach((id) => {
          if (id in next) {
            delete next[id];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      await loadBudgetPage();
      await loadBudgetTotals();
      setBudgetDeleteResult(systemText[language].deletedSuccessfully);
    } catch {
      // Keep current UI state if bulk delete fails.
    } finally {
      setIsDeletingSelectedBudgetRows(false);
    }
  };

  const startEditSpending = (item: Spending) => {
    setEditingSpendingId(item.id);
    setSpendingForm({
      item: item.item,
      budget: String(item.budget),
      budgetCurrency: item.budgetCurrency,
      actual: String(item.actual),
      actualCurrency: item.actualCurrency,
      note: item.note,
    });
  };

  const openBudgetAmountModal = () => {
    setBudgetDraft({
      budget: String(displayedTotals.budgetTotal),
      actual: String(displayedTotals.actualTotal),
    });
    setBudgetAmountModalOpen(true);
  };

  const saveBudgetAmountModal = () => {
    const budgetValue = toNumber(budgetDraft.budget);
    const actualValue = toNumber(budgetDraft.actual);

    void (async () => {
      const token = localStorage.getItem("wedding_access_token");
      if (!token) {
        return;
      }
      try {
        const hasSavedBudgetTotals = budgetTotalsOverride !== null;
        const payload = {
          budget_cap: budgetCapValue,
          plan_amount_total: budgetValue,
          current_amount_total: actualValue,
          plan_total_riel: 0,
          current_total_riel: 0,
          plan_amount: budgetValue,
          current_amount: actualValue,
          plan_riel: 0,
          current_riel: 0,
        };
        const response = await fetch(buildApiUrl(API_BUDGET_TOTALS_PATH), {
          method: hasSavedBudgetTotals ? "PATCH" : "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (handleUnauthorizedResponse(response)) {
          return;
        }

        if (!response.ok) {
          return;
        }

        const totals = (await response.json()) as BudgetTotalsResponse;
        setBudgetCapValue(totals.budget_cap == null ? BUDGET_CAP : toNumber(totals.budget_cap));
        setBudgetTotalsOverride({
          budgetTotal: toNumber(totals.plan_amount_total),
          actualTotal: toNumber(totals.current_amount_total),
          planUsd: toNumber(totals.plan_total_usd ?? totals.total_usd),
          planRiel: toNumber(totals.plan_total_riel ?? totals.total_riel),
          currentUsd: toNumber(totals.current_total_usd),
          currentRiel: toNumber(totals.current_total_riel),
          leftUsd: toNumber(totals.left_total_usd),
          leftRiel: toNumber(totals.left_total_riel),
        });
        setBudgetAmountModalOpen(false);
      } catch {
        // Keep modal/form state when totals update fails.
      }
    })();
  };

  const handleDownloadBudgetImportSample = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      throw new Error("Please sign in again.");
    }

    const response = await fetch(buildApiUrl(API_BUDGET_ITEMS_IMPORT_TEMPLATE_PATH), {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (handleUnauthorizedResponse(response)) {
      throw new Error("Please sign in again.");
    }

    if (!response.ok) {
      throw new Error("Unable to download sample file.");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const contentDisposition = response.headers.get("Content-Disposition") ?? "";
    const filenameMatch = contentDisposition.match(/filename=\"?([^"]+)\"?/i);
    link.href = url;
    link.download = filenameMatch?.[1] ?? "wedding-budget-item-import-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleUploadBudgetFile = async (file: File) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      throw new Error("Please sign in again.");
    }

    setIsUploadingBudgetFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(buildApiUrl(API_BUDGET_ITEMS_IMPORT_PATH), {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (handleUnauthorizedResponse(response)) {
        throw new Error("Please sign in again.");
      }

      const payload = (await response.json().catch(() => null)) as
        | BudgetItemImportResponse
        | { detail?: string }
        | null;

      if (!response.ok) {
        throw new Error(
          payload && "detail" in payload && payload.detail
            ? payload.detail
            : "Unable to upload budget file.",
        );
      }

      await loadBudgetPage();
      await loadBudgetTotals();

      const importResult = payload as BudgetItemImportResponse;
      const firstError = importResult.errors[0]?.message;
      return {
        created: importResult.created_count,
        failed: importResult.failed_count,
        message:
          importResult.failed_count > 0
            ? `${importResult.created_count} uploaded, ${importResult.failed_count} failed. ${firstError ?? ""}`.trim()
            : `${importResult.created_count} items uploaded successfully.`,
      };
    } finally {
      setIsUploadingBudgetFile(false);
    }
  };

  const fetchRegistryPage = async (
    token: string,
    path: string,
    groupFilter: UploadedDraftGroupFilter,
    page: number,
  ): Promise<RegistryDraftListResponse> => {
    const requestKey = `${path}:${groupFilter}:${page}:${REGISTRY_TABLE_LIMIT}`;
    const now = Date.now();
    const cached = uploadedDraftPageCacheRef.current.get(requestKey);
    if (cached && cached.expiresAt > now) {
      return cached.payload;
    }

    const inFlight = uploadedDraftPageRequestsRef.current.get(requestKey);
    if (inFlight) {
      return inFlight;
    }

    const params = new URLSearchParams({
      guest_group: groupFilter,
      page: String(page),
      limit: String(REGISTRY_TABLE_LIMIT),
    });
    const requestPromise = (async () => {
      const response = await fetch(`${buildApiUrl(path)}?${params.toString()}`, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (hasUnauthorizedStatus(response)) {
        showSessionExpiredModal();
        throw new Error(systemText[language].signInAgainLoadRecords);
      }

      if (!response.ok) {
        throw new Error(systemText[language].unableLoadUploadedRegistryRecords);
      }

      const payload = (await response.json()) as RegistryDraftListResponse;
      uploadedDraftPageCacheRef.current.set(requestKey, {
        expiresAt: Date.now() + 1500,
        payload,
      });
      return payload;
    })();

    uploadedDraftPageRequestsRef.current.set(requestKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      uploadedDraftPageRequestsRef.current.delete(requestKey);
    }
  };

  const fetchAllDraftPages = async (
    token: string,
    path: string,
    groupFilter: UploadedDraftGroupFilter,
  ): Promise<RegistryDraftResponse[]> => {
    const allItems: RegistryDraftResponse[] = [];
    let page = REGISTRY_TABLE_PAGE;

    while (true) {
      const payload = await fetchRegistryPage(token, path, groupFilter, page);
      const pageItems = payload.items ?? [];
      allItems.push(...pageItems);

      const totalPages =
        typeof payload.total_pages === "number" && payload.total_pages > 0
          ? payload.total_pages
          : null;

      if (totalPages != null) {
        if (page >= totalPages) {
          break;
        }
        page += 1;
        continue;
      }

      if (pageItems.length < REGISTRY_TABLE_LIMIT) {
        break;
      }
      page += 1;
    }

    return allItems;
  };

  const fetchWeddingGuestsPage = async (
    token: string,
    groupFilter: UploadedDraftGroupFilter,
    page: number,
    limit = REGISTRY_TABLE_LIMIT,
  ): Promise<GuestsListResponse> => {
    const params = new URLSearchParams({
      group: groupFilter,
      status: "all",
      done: "all",
      invite: "all",
      gift: "all",
      category: "all",
      household: "all",
      page: String(page),
      limit: String(limit),
    });

    const response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}?${params.toString()}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (hasUnauthorizedStatus(response)) {
      showSessionExpiredModal();
      throw new Error(systemText[language].signInAgainLoadRecords);
    }

    if (!response.ok) {
      throw new Error(systemText[language].unableLoadUploadedRegistryRecords);
    }

    return (await response.json()) as GuestsListResponse;
  };

  const fetchAllWeddingGuestPages = async (
    token: string,
    groupFilter: UploadedDraftGroupFilter,
  ): Promise<GuestsListItemResponse[]> => {
    const allItems: GuestsListItemResponse[] = [];
    let page = REGISTRY_TABLE_PAGE;

    while (true) {
      const payload = await fetchWeddingGuestsPage(token, groupFilter, page);
      const pageItems = payload.items ?? [];
      allItems.push(...pageItems);

      const effectiveLimit = toNumber(payload.limit) || REGISTRY_TABLE_LIMIT;
      const totalPages =
        typeof payload.total_pages === "number" && payload.total_pages > 0
          ? payload.total_pages
          : Math.max(1, Math.ceil(toNumber(payload.total) / effectiveLimit));

      if (page >= totalPages || pageItems.length < effectiveLimit) {
        break;
      }

      page += 1;
    }

    return allItems;
  };

  const loadUploadedDrafts = async (groupFilter: UploadedDraftGroupFilter, page = uploadedGuestsPage) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setUploadedGuests([]);
      setUploadedGuestCounts({ all: 0, bride: 0, groom: 0 });
      setUploadedGuestsTotalPages(1);
      setUploadedGuestsError(systemText[language].signInAgainLoadRecords);
      return;
    }

    const requestId = ++uploadedGuestsRequestIdRef.current;
    setIsLoadingUploadedGuests(true);
    setUploadedGuestsError("");
    try {
      const [allPayload, bridePayload, groomPayload, currentPayload] = await Promise.all([
        fetchWeddingGuestsPage(token, "all", 1),
        fetchWeddingGuestsPage(token, "bride", 1),
        fetchWeddingGuestsPage(token, "groom", 1),
        fetchWeddingGuestsPage(token, groupFilter, page),
      ]);
      if (requestId !== uploadedGuestsRequestIdRef.current) {
        return;
      }

      setUploadedGuestCounts({
        all: toNumber(allPayload.total),
        bride: toNumber(bridePayload.total),
        groom: toNumber(groomPayload.total),
      });
      setUploadedGuests((currentPayload.items ?? []).map(mapApiGuestToGuest));
      setUploadedGuestsTotalPages(
        typeof currentPayload.total_pages === "number" && currentPayload.total_pages > 0
          ? currentPayload.total_pages
          : Math.max(
              1,
              Math.ceil(
                toNumber(currentPayload.total) /
                  (toNumber(currentPayload.limit) || REGISTRY_TABLE_LIMIT),
              ),
            ),
      );
    } catch (error) {
      if (requestId !== uploadedGuestsRequestIdRef.current) {
        return;
      }
      setUploadedGuests([]);
      setUploadedGuestCounts({ all: 0, bride: 0, groom: 0 });
      setUploadedGuestsTotalPages(1);
      setUploadedGuestsError(
        error instanceof Error
          ? error.message
          : systemText[language].unableLoadUploadedRegistryRecords,
      );
    } finally {
      if (requestId === uploadedGuestsRequestIdRef.current) {
        setIsLoadingUploadedGuests(false);
      }
    }
  };

  const searchUploadedDrafts = async (query: string, groupFilter: UploadedDraftGroupFilter) => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setUploadedGuests([]);
      setUploadedGuestsError(systemText[language].signInAgainSearchRecords);
      return;
    }

    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      await loadUploadedDrafts(groupFilter);
      return;
    }

    const requestId = ++uploadedGuestsRequestIdRef.current;
    setIsLoadingUploadedGuests(true);
    setUploadedGuestsError("");
    try {
      const items = await fetchAllWeddingGuestPages(token, groupFilter);
      if (requestId !== uploadedGuestsRequestIdRef.current) {
        return;
      }
      const mapped = items.map(mapApiGuestToGuest);
      setUploadedGuests(filterRegistryGuestsByQuery(mapped, normalizedQuery));
      setUploadedGuestsTotalPages(1);
    } catch (error) {
      if (requestId !== uploadedGuestsRequestIdRef.current) {
        return;
      }
      setUploadedGuests([]);
      setUploadedGuestsError(
        error instanceof Error ? error.message : systemText[language].unableSearchRegistryRecords,
      );
    } finally {
      if (requestId === uploadedGuestsRequestIdRef.current) {
        setIsLoadingUploadedGuests(false);
      }
    }
  };

  const handleUploadedGuestsPageChange = (page: number) => {
    setUploadedGuestsPage(page);
  };

  const handleAttendancePageChange = (page: number) => {
    setAttendancePage(page);
  };

  const fetchSameNameGuestsPage = async (
    token: string,
    page: number,
    gift: SameNameGiftFilter = "all",
    limit = REGISTRY_TABLE_LIMIT,
  ) => {
    const params = new URLSearchParams({
      group: "all",
      status: "all",
      done: "all",
      invite: "all",
      gift,
      category: "all",
      household: "all",
      page: String(page),
      limit: String(limit),
    });
    const response = await fetch(
      `${buildApiUrl(API_GUESTS_SEARCH_NAME_PATH)}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (handleUnauthorizedResponse(response)) {
      return null;
    }

    const payload = (await response.json().catch(() => null)) as
      | GuestsSearchByNameResponse
      | { detail?: string; message?: string }
      | null;

    if (!response.ok || !payload || !("items" in payload)) {
      throw new Error(
        payload && "message" in payload
          ? payload.detail || payload.message || systemText[language].unableLoadUploadedRegistryRecords
          : systemText[language].unableLoadUploadedRegistryRecords,
      );
    }

    return payload;
  };

  const fetchSameNameGuests = async (token: string, gift: SameNameGiftFilter = "all") => {
    const allItems: GuestsListItemResponse[] = [];
    let firstPayload: GuestsSearchByNameResponse | null = null;
    let page = REGISTRY_TABLE_PAGE;

    while (true) {
      const payload = await fetchSameNameGuestsPage(token, page, gift);
      if (!payload) {
        return null;
      }

      if (!firstPayload) {
        firstPayload = payload;
      }

      const pageItems = payload.items ?? [];
      allItems.push(...pageItems);

      const totalPages =
        typeof payload.total_pages === "number" && payload.total_pages > 0
          ? payload.total_pages
          : null;

      if (totalPages != null) {
        if (page >= totalPages) {
          break;
        }
        page += 1;
        continue;
      }

      if (pageItems.length < REGISTRY_TABLE_LIMIT) {
        break;
      }
      page += 1;
    }

    return {
      ...(firstPayload ?? {
        total: 0,
        invited_count: 0,
        with_gift_count: 0,
        no_gift_count: 0,
        page: 1,
        limit: REGISTRY_TABLE_LIMIT,
        total_pages: 1,
      }),
      items: allItems,
      total: firstPayload?.total ?? allItems.length,
    };
  };

  const applyRegistrySameNamePage = (rows: Guest[], page: number) => {
    const next = paginateRows(rows, page, REGISTRY_TABLE_LIMIT);
    setUploadedGuests(next.items);
    setUploadedGuestsTotalPages(next.totalPages);
    if (next.page !== uploadedGuestsPage) {
      setUploadedGuestsPage(next.page);
    }
  };

  const applyAttendanceSameNamePage = (rows: Guest[], page: number) => {
    const next = paginateRows(rows, page, ATTENDANCE_TABLE_LIMIT);
    setAttendanceGuests(next.items);
    setAttendanceDoneIds(next.items.filter((guest) => guest.done).map((guest) => guest.id));
    setAttendanceTotalPages(next.totalPages);
    setAttendanceCounts({
      total: rows.length,
      invited_count: rows.filter((guest) => Boolean(guest.invited)).length,
      with_gift_count: rows.filter((guest) => (guest.giftAmount ?? 0) > 0).length,
    });
    if (next.page !== attendancePage) {
      setAttendancePage(next.page);
    }
  };

  const refreshSameNameCount = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setSameNameGiftCounts({ withGift: 0, noGift: 0 });
      return;
    }

    try {
      const payload = await fetchSameNameGuests(token);
      if (!payload) {
        return;
      }
      setSameNameGiftCounts({
        withGift: payload.with_gift_count ?? 0,
        noGift: payload.no_gift_count ?? 0,
      });
    } catch {
      setSameNameGiftCounts({
        withGift: 0,
        noGift: 0,
      });
    }
  };

  const loadSameNameGuests = async (gift: SameNameGiftFilter = "all") => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setUploadedGuests([]);
      setUploadedGuestsError(systemText[language].signInAgainLoadRecords);
      setSameNameGiftCounts({
        withGift: 0,
        noGift: 0,
      });
      return;
    }

    setIsLoadingUploadedGuests(true);
    setUploadedGuestsError("");
    setRegistryDataMode("same-name");
    setRegistrySameNameGiftFilter(gift);
    setUploadedGroupFilter("all");
    setShowUploadedTable(true);
    setRegistrySearch("");
    setUploadedGuestsPage(1);
    const requestId = ++uploadedGuestsRequestIdRef.current;
    try {
      const payload = await fetchSameNameGuests(token, gift);
      if (!payload) {
        return;
      }
      if (requestId !== uploadedGuestsRequestIdRef.current) {
        return;
      }
      setSameNameGiftCounts({
        withGift: payload.with_gift_count ?? 0,
        noGift: payload.no_gift_count ?? 0,
      });
      const mapped = (payload.items ?? []).map(mapApiGuestToGuest);
      setRegistrySameNameRows(mapped);
      applyRegistrySameNamePage(mapped, 1);
    } catch (error) {
      if (requestId !== uploadedGuestsRequestIdRef.current) {
        return;
      }
      setRegistrySameNameRows([]);
      setUploadedGuests([]);
      setUploadedGuestsTotalPages(1);
      setUploadedGuestsError(
        error instanceof Error
          ? error.message
          : systemText[language].unableLoadUploadedRegistryRecords,
      );
      setSameNameGiftCounts({
        withGift: 0,
        noGift: 0,
      });
    } finally {
      if (requestId === uploadedGuestsRequestIdRef.current) {
        setIsLoadingUploadedGuests(false);
      }
    }
  };

  const handleUploadedFilterChange = (groupFilter: UploadedDraftGroupFilter) => {
    setRegistryDataMode("drafts");
    setRegistrySameNameRows([]);
    setUploadedGroupFilter(groupFilter);
    setUploadedGuestsPage(1);
    setRegistrySearch("");
    setShowUploadedTable(true);
  };

  const handleRegistrySearchChange = (value: string) => {
    setRegistryDataMode("drafts");
    setRegistrySameNameRows([]);
    setUploadedGuestsPage(1);
    setRegistrySearch(value);
  };

  const loadAllDrafts = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setGuests([]);
      return;
    }

    try {
      const items = await fetchAllDraftPages(token, API_REGISTRY_DRAFTS_PATH, "all");
      setGuests(items.map(mapDraftToGuest));
    } catch {
      setGuests([]);
    }
  };

  const loadBudgetTotals = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(API_BUDGET_TOTALS_PATH), {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (handleUnauthorizedResponse(response)) {
        return;
      }

      if (!response.ok) {
        return;
      }

      const totals = (await response.json()) as BudgetTotalsResponse;
      setBudgetCapValue(totals.budget_cap == null ? BUDGET_CAP : toNumber(totals.budget_cap));
      setBudgetTotalsOverride({
        budgetTotal: toNumber(totals.plan_amount_total),
        actualTotal: toNumber(totals.current_amount_total),
        planUsd: toNumber(totals.plan_total_usd ?? totals.total_usd),
        planRiel: toNumber(totals.plan_total_riel ?? totals.total_riel),
        currentUsd: toNumber(totals.current_total_usd),
        currentRiel: toNumber(totals.current_total_riel),
        leftUsd: toNumber(totals.left_total_usd),
        leftRiel: toNumber(totals.left_total_riel),
      });
    } catch {
      // Keep current totals when API is unavailable.
    }
  };

  const loadBudgetPage = async () => {
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(API_BUDGET_PAGE_PATH), {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (handleUnauthorizedResponse(response)) {
        return;
      }

      if (!response.ok) {
        return;
      }

      const payload = (await response.json()) as BudgetPageResponse;
      const mappedSpending: Spending[] = (payload.items ?? []).map((item) => {
        const override = budgetCurrencyOverrides[item.id];
        return {
          id: item.id,
          item: item.item,
          budget: toNumber(item.plan_amount),
          budgetCurrency:
            item.plan_currency != null
              ? normalizeGiftCurrency(item.plan_currency)
              : override?.budgetCurrency ?? "USD",
          actual: toNumber(item.current_amount),
          actualCurrency:
            item.current_currency != null
              ? normalizeGiftCurrency(item.current_currency)
              : override?.actualCurrency ?? "USD",
          note: item.notes ?? "",
        };
      });

      setSpending(mappedSpending);
      setBudgetCapValue(payload.totals?.budget_cap == null ? BUDGET_CAP : toNumber(payload.totals.budget_cap));
      setBudgetTotalsOverride({
        budgetTotal: toNumber(payload.totals?.plan_amount_total),
        actualTotal: toNumber(payload.totals?.current_amount_total),
        planUsd: toNumber(payload.totals?.plan_total_usd ?? payload.totals?.total_usd),
        planRiel: toNumber(payload.totals?.plan_total_riel ?? payload.totals?.total_riel),
        currentUsd: toNumber(payload.totals?.current_total_usd),
        currentRiel: toNumber(payload.totals?.current_total_riel),
        leftUsd: toNumber(payload.totals?.left_total_usd),
        leftRiel: toNumber(payload.totals?.left_total_riel),
      });
    } catch {
      // Keep existing UI state on budget fetch failure.
    }
  };

  const registryGuests = useMemo(() => {
    return guests.filter((guest) => {
      if ((guest.source ?? "local") !== "local") {
        return false;
      }
      if (uploadedGroupFilter === "all") {
        return true;
      }
      return GROUP_TO_API[guest.group] === uploadedGroupFilter;
    });
  }, [guests, uploadedGroupFilter]);

  const statusTextClass = (status: GuestStatus) => {
    if (status === "Joining") {
      return "text-emerald-500";
    }
    if (status === "Maybe") {
      return "text-amber-500";
    }
    return "text-rose-500";
  };

  const statusBadgeClass = (status: GuestStatus) => {
    if (status === "Joining") {
      return "bg-emerald-100 text-emerald-800";
    }
    if (status === "Maybe") {
      return "bg-amber-100 text-amber-800";
    }
    return "bg-rose-100 text-rose-800";
  };

  const statusSelectClass = (status: GuestStatus) => {
    if (status === "Joining") {
      return "border-emerald-300 bg-emerald-50 text-emerald-700";
    }
    if (status === "Maybe") {
      return "border-amber-300 bg-amber-50 text-amber-700";
    }
    return "border-rose-300 bg-rose-50 text-rose-700";
  };

  const selectedGuest = useMemo(
    () => guests.find((guest) => guest.id === mapGuestId) ?? null,
    [guests, mapGuestId],
  );
  const mapsUnavailableMessage = !hasMapsApiKey
    ? "Google Maps is unavailable because NEXT_PUBLIC_GOOGLE_MAPS_API_KEY was not provided at build time."
    : "";

  useEffect(() => {
    const isDarkEnabled = document.documentElement.classList.contains("dark");
    setIsDark(isDarkEnabled);
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage === "en" || savedLanguage === "kh") {
      setLanguage(savedLanguage);
    }
    const savedCategoryTranslations = localStorage.getItem(CATEGORY_TRANSLATIONS_STORAGE_KEY);
    if (savedCategoryTranslations) {
      try {
        const parsed = JSON.parse(savedCategoryTranslations) as Record<string, string>;
        if (parsed && typeof parsed === "object") {
          setCategoryTranslations(parsed);
        }
      } catch {}
    }
    const savedToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (isStoredTokenValid(savedToken)) {
      const requestedTab = new URLSearchParams(window.location.search).get("tab");
      const savedTab = localStorage.getItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY);
      const tabToRestore = isDashboardTab(requestedTab)
        ? requestedTab
        : isDashboardTab(savedTab)
          ? savedTab
          : "registry";

      setActiveTab(tabToRestore);
      localStorage.setItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY, tabToRestore);
      setIsAuthed(true);
    } else {
      const requestedAccess = new URLSearchParams(window.location.search).get("access");
      if (requestedAccess === "request") {
        setIsAccessRequestPageOpen(true);
      }
      clearStoredAuthToken();
      localStorage.removeItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY);
    }
    setIsAuthInitialized(true);
  }, []);

  useEffect(() => {
    if (!mapOpen || !mapsReady || !mapRef.current || !selectedGuest) {
      return;
    }
    if (mapInstanceRef.current) {
      return;
    }

    const google = (window as Window & { google?: GoogleMapsApi }).google;
    if (!google?.maps) {
      return;
    }
    const maps = google.maps;

    const initialLat = selectedGuest.latitude ?? 11.5564;
    const initialLng = selectedGuest.longitude ?? 104.9282;
    const initial = { lat: initialLat, lng: initialLng };

    const map = new maps.Map(mapRef.current, {
      center: initial,
      zoom: selectedGuest.latitude && selectedGuest.longitude ? 14 : 6,
      disableDefaultUI: true,
      zoomControl: true,
    });
    mapInstanceRef.current = map;

    if (selectedGuest.latitude && selectedGuest.longitude) {
      markerRef.current = new maps.Marker({
        position: initial,
        map,
      });
    }

    map.addListener("click", (event: { latLng: { lat: () => number; lng: () => number } }) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      if (!markerRef.current) {
        markerRef.current = new maps.Marker({
          position: { lat, lng },
          map,
        });
      } else {
        markerRef.current.setPosition({ lat, lng });
      }
      updateGuest(selectedGuest.id, { latitude: lat, longitude: lng });
    });
  }, [mapOpen, mapsReady, selectedGuest]);

  useEffect(() => {
    if (!mapOpen) {
      mapInstanceRef.current = null;
      markerRef.current = null;
      setLocationError("");
    }
  }, [mapOpen]);

  useEffect(() => {
    if (!isAuthed || activeTab !== "registry" || !showUploadedTable) return;
    if (registryDataMode === "same-name") return;
    const trimmed = registrySearch.trim();
    const requestSignature = [
      activeTab,
      registryDataMode,
      uploadedGroupFilter,
      trimmed,
      uploadedGuestsPage,
      uploadedDraftsReloadKey,
    ].join("|");
    if (uploadedDraftsRequestSignatureRef.current === requestSignature) {
      return;
    }
    uploadedDraftsRequestSignatureRef.current = requestSignature;
    const timer = window.setTimeout(() => {
      if (trimmed) {
        void searchUploadedDrafts(trimmed, uploadedGroupFilter);
      } else {
        void loadUploadedDrafts(uploadedGroupFilter, uploadedGuestsPage);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [
    isAuthed,
    activeTab,
    showUploadedTable,
    uploadedGroupFilter,
    registrySearch,
    registryDataMode,
    uploadedGuestsPage,
    uploadedDraftsReloadKey,
  ]);

  useEffect(() => {
    if (!isAuthed || activeTab !== "registry" || !showUploadedTable) return;
    if (registryDataMode !== "same-name") return;
    applyRegistrySameNamePage(registrySameNameRows, uploadedGuestsPage);
  }, [
    isAuthed,
    activeTab,
    showUploadedTable,
    registryDataMode,
    registrySameNameGiftFilter,
    registrySameNameRows,
    uploadedGuestsPage,
  ]);

  useEffect(() => {
    if (!isAuthed || activeTab !== "registry") return;
    void refreshSameNameCount();
  }, [isAuthed, activeTab, uploadedBatches, registryUpdateResult]);

  useEffect(() => {
    attendanceRequestSignatureRef.current = null;
    setAttendanceDataMode("default");
    setAttendanceSameNameRows([]);
    setAttendanceGuests([]);
    setAttendanceDoneIds([]);
    setAttendanceTotalPages(1);
    setAttendancePage(1);
  }, [
    attendanceFilter,
    attendanceDoneFilter,
    attendanceInviteFilter,
    attendanceGiftFilter,
    attendanceHouseholdFilter,
    attendanceCategoryFilter,
    attendanceSearch,
  ]);

  useEffect(() => {
    if (!isAuthed || activeTab !== "attendance") return;
    if (attendanceDataMode !== "default") {
      applyAttendanceSameNamePage(attendanceSameNameRows, attendancePage);
      return;
    }
    const timer = window.setTimeout(() => {
      void loadAttendanceGuests();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    isAuthed,
    activeTab,
    attendanceDataMode,
    attendanceFilter,
    attendanceDoneFilter,
    attendanceInviteFilter,
    attendanceGiftFilter,
    attendanceHouseholdFilter,
    attendanceCategoryFilter,
    attendanceSearch,
    attendanceSameNameRows,
  ]);

  useEffect(() => {
    if (!isAuthed) return;
    void loadAllDrafts();
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed || activeTab !== "budget") return;
    void loadBudgetPage();
    void loadBudgetTotals();
  }, [isAuthed, activeTab]);

  useEffect(() => {
    return () => {
      if (weddingMusicRef.current) {
        weddingMusicRef.current.pause();
        weddingMusicRef.current.currentTime = 0;
      }
    };
  }, []);

  const showLoginBackground = !isAuthed;
  const changeLanguage = (next: "en" | "kh") => {
    setLanguage(next);
    localStorage.setItem("language", next);
  };
  const toggleTheme = () => {
    setIsDark((prev) => {
      const next = !prev;
      document.documentElement.classList.toggle("dark", next);
      localStorage.setItem("theme", next ? "dark" : "light");
      return next;
    });
  };

  const ensureWeddingMusicPlayer = () => {
    if (!weddingMusicRef.current) {
      const audio = new Audio(WEDDING_THEME_SONG_URL);
      audio.loop = true;
      audio.preload = "auto";
      audio.addEventListener("play", () => setIsMusicPlaying(true));
      audio.addEventListener("pause", () => setIsMusicPlaying(false));
      audio.addEventListener("ended", () => setIsMusicPlaying(false));
      weddingMusicRef.current = audio;
    }
    return weddingMusicRef.current;
  };

  const playWeddingMusic = async () => {
    const audio = ensureWeddingMusicPlayer();
    try {
      await audio.play();
    } catch {
      setIsMusicPlaying(false);
    }
  };

  const toggleWeddingMusic = async () => {
    const audio = ensureWeddingMusicPlayer();

    if (audio.paused) {
      await playWeddingMusic();
      return;
    }

    audio.pause();
  };

  useEffect(() => {
    if (!isAuthed) {
      if (weddingMusicRef.current) {
        weddingMusicRef.current.pause();
      }
      return;
    }
    void playWeddingMusic();
  }, [isAuthed]);

  const openGiftModal = (guest: Guest) => {
    setGiftGuestId(guest.id);
    setGiftAmountInput(guest.giftAmount != null ? String(guest.giftAmount) : "");
    setGiftCurrencyInput(guest.giftCurrency ?? "Riel");
    setGiftInputError("");
  };

  const doneById = (guestId: number) => attendanceDoneIds.includes(guestId);

  const handleAttendanceStatusChange = (guest: Guest, status: GuestStatus) => {
    setAttendanceGuests((prev) =>
      prev.map((row) => (row.id === guest.id ? { ...row, status } : row)),
    );
    void patchAttendanceGuest(guest.id, { status });
  };

  const handleAttendanceInviteToggle = (guest: Guest) => {
    const invited = !Boolean(guest.invited);
    setAttendanceGuests((prev) =>
      prev.map((row) => (row.id === guest.id ? { ...row, invited } : row)),
    );
    void patchAttendanceGuest(guest.id, { invited });
  };

  const handleAttendanceDoneToggle = (guest: Guest) => {
    const nextDone = !attendanceDoneIds.includes(guest.id);
    setAttendanceDoneIds((prev) =>
      nextDone ? (prev.includes(guest.id) ? prev : [...prev, guest.id]) : prev.filter((id) => id !== guest.id),
    );
    setAttendanceGuests((prev) =>
      prev.map((row) => (row.id === guest.id ? { ...row, done: nextDone } : row)),
    );
    void patchAttendanceGuest(guest.id, { done: nextDone });
  };

  const handleDeleteSelectedAttendanceRows = async (ids: number[]) => {
    if (ids.length === 0) {
      return false;
    }
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      return false;
    }

    setIsDeletingSelectedAttendanceRows(true);
    try {
      const deletedIds: number[] = [];
      for (const id of ids) {
        const response = await fetch(`${buildApiUrl(API_GUESTS_PATH)}/${id}`, {
          method: "DELETE",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (handleUnauthorizedResponse(response)) {
          return false;
        }
        if (!response.ok) {
          return false;
        }
        deletedIds.push(id);
      }

      const deletedSet = new Set(deletedIds);
      if (attendanceDataMode === "same-name-have-gift") {
        const remainingRows = attendanceSameNameRows.filter((guest) => !deletedSet.has(guest.id));
        setAttendanceSameNameRows(remainingRows);
        const remainingRowsOnPage = attendanceGuests.filter((guest) => !deletedSet.has(guest.id)).length;
        const nextPage = remainingRowsOnPage === 0 && attendancePage > 1 ? attendancePage - 1 : attendancePage;
        applyAttendanceSameNamePage(remainingRows, nextPage);
      } else {
        const remainingRowsOnPage = attendanceGuests.filter((guest) => !deletedSet.has(guest.id)).length;
        const nextPage = remainingRowsOnPage === 0 && attendancePage > 1 ? attendancePage - 1 : attendancePage;

        if (nextPage !== attendancePage) {
          setAttendancePage(nextPage);
        } else {
          await loadAttendanceGuests();
        }
      }
      return true;
    } catch {
      // Keep current attendance list if bulk delete fails.
      return false;
    } finally {
      setIsDeletingSelectedAttendanceRows(false);
    }
  };

  const handleSaveGift = () => {
    if (giftGuestId == null) {
      return;
    }
    const parsed = Number(giftAmountInput);
    if (giftAmountInput.trim() && Number.isNaN(parsed)) {
      setGiftInputError(systemText[language].textNotAllowedAmountOnly);
      return;
    }
    setGiftInputError("");
    const nextGiftAmount = giftAmountInput.trim() ? parsed : null;
    setAttendanceGuests((prev) =>
      prev.map((row) =>
        row.id === giftGuestId
          ? {
              ...row,
              giftAmount: nextGiftAmount,
              giftCurrency: giftCurrencyInput,
            }
          : row,
      ),
    );
    void patchAttendanceGuest(giftGuestId, {
      giftAmount: nextGiftAmount,
      giftCurrency: giftCurrencyInput,
    });
    setGiftGuestId(null);
    setGiftAmountInput("");
    setGiftCurrencyInput("Riel");
    setGiftInputError("");
  };

  const handleUseCurrentLocation = () => {
    if (!selectedGuest) {
      setLocationError(systemText[language].selectGuestBeforeLocation);
      return;
    }
    if (!navigator.geolocation) {
      setLocationError(systemText[language].geolocationNotSupported);
      return;
    }
    if (!window.isSecureContext) {
      setLocationError(systemText[language].locationRequiresSecureContext);
      return;
    }
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const google = (window as Window & { google?: GoogleMapsApi }).google;
        if (google?.maps && mapInstanceRef.current) {
          const maps = google.maps;
          const coords = { lat, lng };
          mapInstanceRef.current.setCenter(coords);
          mapInstanceRef.current.setZoom(14);
          if (!markerRef.current) {
            markerRef.current = new maps.Marker({
              position: coords,
              map: mapInstanceRef.current,
            });
          } else {
            markerRef.current.setPosition(coords);
          }
        }
        updateGuest(selectedGuest.id, { latitude: lat, longitude: lng });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError(systemText[language].permissionDeniedLocation);
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setLocationError(systemText[language].locationUnavailableTryAgain);
        } else if (error.code === error.TIMEOUT) {
          setLocationError(systemText[language].locationRequestTimedOut);
        } else {
          setLocationError(systemText[language].unableAccessCurrentLocation);
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  if (!isAuthInitialized) {
    return null;
  }

  return (
    <div
      onClickCapture={handleAuthenticatedInteractionCapture}
      className={`relative min-h-svh overflow-x-hidden overflow-y-auto px-3 py-4 sm:px-6 sm:py-8 ${
        showLoginBackground ? "bg-transparent" : "bg-[color:var(--background)]"
      }`}
    >
      {hasMapsApiKey ? (
        <Script
          src={`https://maps.googleapis.com/maps/api/js?key=${mapsApiKey}`}
          strategy="afterInteractive"
          onLoad={() => setMapsReady(true)}
          onError={() =>
            setLocationError(
              "Google Maps failed to load. Check the API key and Google Cloud referrer restrictions.",
            )
          }
        />
      ) : null}
      {showLoginBackground ? (
        <div
          className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/images/bg.jpg')" }}
        >
          <div
            className="absolute inset-0"
            style={{ background: "var(--video-overlay)" }}
          />
          <div
            className="absolute inset-0"
            style={{ backgroundImage: "var(--video-glow)" }}
          />
        </div>
      ) : (
        <div
          className="absolute inset-0 -z-10"
          style={{ backgroundImage: "var(--page-backdrop)" }}
        />
      )}
      <div
        className={`fixed z-40 flex items-center gap-2 ${
          isAuthed ? "bottom-1.5 right-1.5 sm:bottom-2 sm:right-2" : "right-3 top-3 sm:right-6 sm:top-6"
        }`}
      >
        {isAuthed ? (
          <MusicToggleButton
            isPlaying={isMusicPlaying}
            onToggle={() => void toggleWeddingMusic()}
            playLabel={language === "kh" ? "ចាក់តន្ត្រីមង្គលការ" : "Play wedding music"}
            pauseLabel={language === "kh" ? "ផ្អាកតន្ត្រីមង្គលការ" : "Pause wedding music"}
          />
        ) : null}
        {!isAuthed ? (
          <button
            type="button"
            onClick={() => window.location.assign("/homepage")}
            aria-label={language === "kh" ? "ទំព័រដើម" : "Homepage"}
            className="group inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] shadow-[var(--toggle-shadow)] transition hover:bg-[color:var(--surface-hover)]"
            title={language === "kh" ? "ទំព័រដើម" : "Homepage"}
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--accent)] text-white transition group-hover:scale-105">
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                <path d="M12 3.5a1.75 1.75 0 0 1 1.166.445l7 6.334a1.75 1.75 0 0 1 .584 1.299V19A1.75 1.75 0 0 1 19 20.75h-4.25A1.75 1.75 0 0 1 13 19v-3.25a.75.75 0 0 0-.75-.75h-.5a.75.75 0 0 0-.75.75V19a1.75 1.75 0 0 1-1.75 1.75H5A1.75 1.75 0 0 1 3.25 19v-7.422c0-.492.207-.96.584-1.299l7-6.334A1.75 1.75 0 0 1 12 3.5Z" />
              </svg>
            </span>
          </button>
        ) : null}
        {!isAuthed ? (
          <LanguageToggleButton
            language={language}
            onChangeLanguage={changeLanguage}
            className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--foreground)] shadow-[var(--toggle-shadow)] transition hover:bg-[color:var(--surface-hover)]"
          />
        ) : null}
        {!isAuthed ? (
          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Toggle dark mode"
            aria-pressed={isDark}
            className="group inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] shadow-[var(--toggle-shadow)] transition hover:bg-[color:var(--surface-hover)]"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--accent)] text-white transition group-hover:scale-105">
              {isDark ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                  <path d="M12 17.75a.75.75 0 0 1 .75.75V20a.75.75 0 0 1-1.5 0v-1.5a.75.75 0 0 1 .75-.75Zm5.303-2.053 1.061 1.06a.75.75 0 1 1-1.06 1.061l-1.061-1.06a.75.75 0 1 1 1.06-1.061ZM12 5.5A6.5 6.5 0 1 1 5.5 12 6.507 6.507 0 0 1 12 5.5Zm8 5.75a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1 0-1.5Zm-14.5 0a.75.75 0 0 1 0 1.5H4a.75.75 0 0 1 0-1.5Zm11.742-5.614a.75.75 0 0 1 1.06 0 .75.75 0 0 1 0 1.06l-1.06 1.061a.75.75 0 1 1-1.061-1.06ZM6.758 15.697a.75.75 0 0 1 1.061 1.06l-1.06 1.061a.75.75 0 1 1-1.061-1.06ZM12 3.25a.75.75 0 0 1 .75.75V5.5a.75.75 0 0 1-1.5 0V4a.75.75 0 0 1 .75-.75Zm-5.303 2.386 1.061 1.06a.75.75 0 0 1-1.06 1.061L5.636 6.697a.75.75 0 1 1 1.06-1.061Z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                  <path d="M20.742 13.045a.75.75 0 0 0-.83-.19 7.39 7.39 0 0 1-2.787.544 7.66 7.66 0 0 1-7.649-7.649c0-.973.187-1.915.544-2.787a.75.75 0 0 0-.95-1 9.1 9.1 0 1 0 11.862 11.861.75.75 0 0 0-.19-.779Z" />
                </svg>
              )}
            </span>
          </button>
        ) : null}
      </div>
      <div className="relative mx-auto w-full max-w-none pb-16 sm:pb-12">
        {isAuthed ? (
          <div className="absolute inset-x-6 top-0 h-1 rounded-full bg-[color:var(--accent)]/80" />
        ) : null}

        {!isAuthed ? (
          <section className="relative z-10 flex min-h-[calc(100svh-2rem)] items-start justify-center pt-14 sm:min-h-[calc(100svh-3rem)] sm:pt-20">
            <div className={`w-full rounded-3xl border border-[color:var(--panel-border)] bg-[color:var(--card)] p-5 shadow-[var(--panel-shadow)] sm:p-8 ${
              isAccessRequestPageOpen ? "max-w-3xl" : "max-w-md"
            }`}>
              {isAccessRequestPageOpen ? (
                <div className="grid gap-5">
                  <style>
                    {`
                      @keyframes access-card-float {
                        0%, 100% {
                          transform: perspective(900px) rotateX(0deg) rotateY(0deg) translateY(0);
                        }
                        50% {
                          transform: perspective(900px) rotateX(1.5deg) rotateY(-1.5deg) translateY(-5px);
                        }
                      }
                    `}
                  </style>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className="font-[family-name:var(--font-display)] text-2xl text-[color:var(--foreground)]">
                        {language === "kh" ? "ស្នើសុំប្រើប្រាស់" : "Request access"}
                      </h2>
                      <p className="mt-2 text-sm leading-6 text-[color:var(--muted)]">
                        {language === "kh"
                          ? "បញ្ចូលទំនាក់ទំនង ដើម្បីបន្ត។"
                          : "Enter contact info to continue."}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAccessRequestPageOpen(false);
                        setSampleRequestNotice("");
                      }}
                      className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                    >
                      {language === "kh" ? "ត្រឡប់" : "Back"}
                    </button>
                  </div>

                  <div className="grid items-start gap-4 md:grid-cols-2">
                    <form
                      className="grid gap-4 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-4 shadow-[0_24px_55px_rgba(15,23,42,0.16)] transition hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(15,23,42,0.22)]"
                      onSubmit={handleSampleAccessRequest}
                      style={{
                        animation: "access-card-float 6s ease-in-out infinite",
                        transformStyle: "preserve-3d",
                      }}
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
                          {language === "kh" ? "គណនីសាកល្បង" : "Sample login"}
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-[color:var(--muted)]">
                          {language === "kh"
                            ? "ផ្ញើទំនាក់ទំនង រួចមើល login។"
                            : "Send contact, then view login."}
                        </p>
                      </div>
                      <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
                        {language === "kh" ? "ឈ្មោះពេញ" : "Full Name"}
                        <input
                          value={sampleFullName}
                          onChange={(event) => {
                            setSampleFullName(event.target.value);
                            setSampleRequestNotice("");
                          }}
                          className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                          placeholder={language === "kh" ? "ឈ្មោះរបស់អ្នក" : "Your name"}
                        />
                      </label>
                      <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
                        {language === "kh" ? "ទូរស័ព្ទ ឬ Telegram" : "Phone or Telegram"}
                        <input
                          value={sampleContact}
                          onChange={(event) => {
                            setSampleContact(event.target.value);
                            setSampleRequestNotice("");
                          }}
                          className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                          placeholder={language === "kh" ? "012 345 678 ឬ @username" : "012 345 678 or @username"}
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={isSendingSampleRequest}
                        className="rounded-full bg-[color:var(--accent)] px-5 py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500"
                      >
                        {isSendingSampleRequest
                          ? language === "kh"
                            ? "កំពុងផ្ញើ..."
                            : "Sending..."
                          : language === "kh"
                            ? "ផ្ញើ និងបង្ហាញ"
                            : "Send and show"}
                      </button>
                      {sampleCredentialsUnlocked ? (
                        <div className="rounded-2xl border border-[color:var(--panel-border)] bg-[color:var(--card)] p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--muted)]">
                            {language === "kh" ? "គណនីសាកល្បង" : "Test account"}
                          </p>
                          <div className="mt-3 grid gap-2 text-sm text-[color:var(--foreground)]">
                            <p>
                              <span className="font-semibold">Username:</span>{" "}
                              <span className="font-mono">admin</span>
                            </p>
                            <p>
                              <span className="font-semibold">Password:</span>{" "}
                              <span className="font-mono">admin1234</span>
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setLoginUsername("admin");
                              setLoginPassword("admin1234");
                              setIsAccessRequestPageOpen(false);
                            }}
                            className="mt-4 w-full rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-2.5 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                          >
                            {language === "kh" ? "ប្រើគណនីនេះ" : "Use this login"}
                          </button>
                        </div>
                      ) : null}
                      {sampleRequestNotice ? (
                        <p className="text-sm text-[color:var(--muted)]">{sampleRequestNotice}</p>
                      ) : null}
                    </form>

                    <div
                      className="grid gap-4 rounded-2xl border border-[color:var(--accent)] bg-[color:var(--surface)] p-4 shadow-[0_24px_55px_rgba(15,23,42,0.16)] transition hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(15,23,42,0.22)]"
                      style={{
                        animation: "access-card-float 6s ease-in-out infinite",
                        animationDelay: "0.7s",
                        transformStyle: "preserve-3d",
                      }}
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
                          {language === "kh" ? "ប្រើគម្រោងនេះ" : "Use this project"}
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-[color:var(--muted)]">
                          {language === "kh"
                            ? "ផ្ញើសំណើទៅម្ចាស់គម្រោង។"
                            : "Send a request to the owner."}
                        </p>
                      </div>
                      <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
                        <span>
                          {language === "kh" ? "ឈ្មោះពេញ" : "Full Name"}{" "}
                          <span className="text-red-500" aria-hidden="true">*</span>
                        </span>
                        <input
                          value={projectFullName}
                          onChange={(event) => {
                            setProjectFullName(event.target.value);
                            setProjectRequiredNotice("");
                          }}
                          required
                          className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                          placeholder={language === "kh" ? "ឈ្មោះរបស់អ្នក" : "Your name"}
                        />
                      </label>
                      <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
                        <span>
                          {language === "kh" ? "ទូរស័ព្ទ ឬ Telegram" : "Phone or Telegram"}{" "}
                          <span className="text-red-500" aria-hidden="true">*</span>
                        </span>
                        <input
                          value={projectContact}
                          onChange={(event) => {
                            setProjectContact(event.target.value);
                            setProjectRequiredNotice("");
                          }}
                          required
                          className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                          placeholder={language === "kh" ? "012 345 678 ឬ @username" : "012 345 678 or @username"}
                        />
                      </label>
                      {projectRequiredNotice ? (
                        <p className="text-sm font-medium text-red-600">{projectRequiredNotice}</p>
                      ) : null}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <a
                          href={`https://t.me/${TELEGRAM_OWNER_USERNAME}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-h-12 w-full items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-3 text-center text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                        >
                          {language === "kh" ? "ទាក់ទងក្រុមការងារ" : "Contact the team"}
                        </a>
                        <button
                          type="button"
                          onClick={openPaymentPopup}
                          className="inline-flex min-h-12 w-full items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-3 text-center text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                        >
                          {language === "kh" ? "បង់ប្រាក់" : "Pay"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                <h2 className="text-center font-[family-name:var(--font-display)] text-2xl text-[color:var(--foreground)]">
                  {loginText[language].title}
                </h2>
                <form
                  className="mt-6 grid gap-4"
                  onSubmit={handleLogin}
                >
                  <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
                    {loginText[language].username}
                    <input
                      name="username"
                      value={loginUsername}
                      onChange={(event) => {
                        setLoginUsername(event.target.value);
                        setAuthNotice("");
                      }}
                      className="rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                      placeholder={loginText[language].usernamePlaceholder}
                      autoComplete="username"
                      required
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium text-[color:var(--foreground)]">
                    {loginText[language].password}
                    <div className="relative">
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(event) => {
                          setLoginPassword(event.target.value);
                          setAuthNotice("");
                        }}
                        className="w-full rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 pr-11 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                        placeholder="•••••••••••"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--accent)] text-white transition hover:brightness-110"
                        aria-label={
                          showPassword
                            ? loginText[language].hidePassword
                            : loginText[language].showPassword
                        }
                        title={
                          showPassword
                            ? loginText[language].hidePassword
                            : loginText[language].showPassword
                        }
                      >
                        {showPassword ? (
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="m3 3 18 18" />
                            <path d="M10.58 10.58a2 2 0 1 0 2.83 2.83" />
                            <path d="M9.88 5.09A10.94 10.94 0 0 1 12 4.9c5 0 9.27 3.11 11 7.5a11.8 11.8 0 0 1-1.67 2.92" />
                            <path d="M6.61 6.61C4.24 8 2.43 10 1 12.4a11.83 11.83 0 0 0 7 6.35A11.3 11.3 0 0 0 12 19.1c1.8 0 3.5-.41 5-1.14" />
                          </svg>
                        ) : (
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </label>
                  {authError ? (
                    <p className="text-sm text-red-600">{authError}</p>
                  ) : null}
                  {authNotice ? (
                    <p className="text-sm text-[color:var(--muted)]">{authNotice}</p>
                  ) : null}
                  <button
                    type="submit"
                    disabled={isLoggingIn}
                    className="mt-2 rounded-full bg-[color:var(--accent)] px-6 py-3 text-sm font-semibold uppercase tracking-wide !text-white transition hover:brightness-110 dark:!text-white"
                  >
                    {isLoggingIn ? "Signing in..." : loginText[language].unlock}
                  </button>
                  <button
                    type="button"
                    onClick={handleRequestProjectAccess}
                    className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-6 py-3 text-sm font-semibold tracking-wide text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                  >
                    {language === "kh"
                      ? "ស្នើសុំប្រើគម្រោងនេះ"
                      : "Request to use this project"}
                  </button>
                </form>
                </>
              )}
              </div>
          </section>
        ) : (
          <section className="relative z-10 mt-1 flex min-h-[calc(100svh-5.75rem)] flex-col sm:min-h-[calc(100svh-6.5rem)]">
            <DashboardTopButtons
              tabs={visibleTabs}
              activeTab={activeTab}
              onTabClick={(tab) => {
                setActiveTab(tab);
                localStorage.setItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY, tab);
                setAttendanceViewStatus(null);
              }}
              onGoHome={() => window.location.assign("/homepage")}
              onSignOut={() => setSignOutConfirmOpen(true)}
              isDark={isDark}
              onToggleTheme={toggleTheme}
              language={language}
              onChangeLanguage={changeLanguage}
              registryTabLabel={registryText[language].title}
              attendanceTabLabel={guestsText[language].tab}
              budgetTabLabel={budgetText[language].tab}
              paymentPackageTabLabel={
                language === "kh" ? "កញ្ចប់ទូទាត់ការចំណាយ" : "Payment Package"
              }
              landingPageTabLabel={language === "kh" ? "ទំព័រ Landing" : "Landing Page"}
            />

            <div className="relative mt-4 min-h-0 flex-1">
              <div
                className={`absolute inset-0 ${
                  activeTab === "registry"
                    ? "visible pointer-events-auto opacity-100"
                    : "invisible pointer-events-none opacity-0"
                }`}
              >
                <RegistrySection
                  registryText={registryText}
                  language={language}
                  registryGuests={registryGuests}
                  registrySearch={registrySearch}
                  onRegistrySearchChange={handleRegistrySearchChange}
                  onLoadSameNameGuests={loadSameNameGuests}
                  sameNameGiftCounts={sameNameGiftCounts}
                  addGuestRow={addGuestRow}
                  handleDownloadRegistryExcel={handleDownloadRegistryExcel}
                  uploadedGuests={uploadedGuests}
                  nameErrorIds={nameErrorIds}
                  setNameErrorIds={setNameErrorIds}
                  updateGuest={updateGuest}
                  categories={categories}
                  categoryTranslations={categoryTranslations}
                  statusSelectClass={statusSelectClass}
                  statusTextClass={statusTextClass}
                  removeGuestRow={removeGuestRow}
                  mapInstanceRef={mapInstanceRef}
                  markerRef={markerRef}
                  setMapGuestId={setMapGuestId}
                  setMapOpen={setMapOpen}
                  uploadedBatches={uploadedBatches}
                  handleUploadSnapshot={handleUploadSnapshot}
                  isUploadingDrafts={isUploadingDrafts}
                  showUploadedTable={showUploadedTable}
                  setShowUploadedTable={setShowUploadedTable}
                  uploadedGroupFilter={uploadedGroupFilter}
                  uploadedGuestCounts={uploadedGuestCounts}
                  uploadedGuestsPage={uploadedGuestsPage}
                  uploadedGuestsTotalPages={uploadedGuestsTotalPages}
                  onUploadedGuestsPageChange={handleUploadedGuestsPageChange}
                  onUploadedFilterChange={handleUploadedFilterChange}
                  uploadError={duplicateUploadErrorMessage || uploadError}
                  isLoadingUploadedGuests={isLoadingUploadedGuests}
                  uploadedGuestsError={uploadedGuestsError}
                  statusBadgeClass={statusBadgeClass}
                  handleEditUploadedRow={handleEditUploadedRow}
                  onEditSelectedUploadedRows={handleEditUploadedRows}
                  setUploadedDeleteId={setUploadedDeleteId}
                  onDeleteSelectedLocalRows={removeGuestRows}
                  onDeleteSelectedUploadedRows={handleDeleteSelectedUploadedRows}
                  isDeletingSelectedUploadedRows={isDeletingSelectedUploadedRows}
                  updateResult={registryUpdateResult}
                  onCloseUpdateResult={() => setRegistryUpdateResult(null)}
                />
              </div>

              <div
                className={`absolute inset-0 ${
                  activeTab === "attendance"
                    ? "visible pointer-events-auto opacity-100"
                    : "invisible pointer-events-none opacity-0"
                }`}
              >
                <GuestsSection
                  guestsText={guestsText}
                  language={language}
                  attendanceRows={attendanceRows}
                  totalGuestCount={attendanceCounts.total}
                  withGiftGuestCount={attendanceCounts.with_gift_count ?? 0}
                  attendanceGroupCounts={attendanceGroupCounts}
                  attendanceGroupFilter={attendanceGroupFilter}
                  setAttendanceGroupFilter={setAttendanceGroupFilterWithPageReset}
                  attendanceFilter={attendanceFilter}
                  attendanceDoneFilter={attendanceDoneFilter}
                  setAttendanceDoneFilter={setAttendanceDoneFilterWithPageReset}
                  attendanceInviteFilter={attendanceInviteFilter}
                  setAttendanceInviteFilter={setAttendanceInviteFilterWithPageReset}
                  attendanceGiftFilter={attendanceGiftFilter}
                  setAttendanceGiftFilter={setAttendanceGiftFilterWithPageReset}
                  attendanceHouseholdFilter={attendanceHouseholdFilter}
                  setAttendanceHouseholdFilter={setAttendanceHouseholdFilterWithPageReset}
                  categoryItems={categoryItems}
                  attendanceCategoryFilter={attendanceCategoryFilter}
                  setAttendanceCategoryFilter={setAttendanceCategoryFilterWithPageReset}
                  attendanceSearch={attendanceSearch}
                  onAttendanceSearchChange={handleAttendanceSearchChange}
                  handleDownloadAllGuestsExcel={handleDownloadAllGuestsExcel}
                  isDownloadingAllGuests={isDownloadingAllGuests}
                  onLoadGuestsWithGift={loadGuestsWithGift}
                  onShowSameNameHaveGift={showAttendanceSameNameHaveGift}
                  onShowAllGuests={showDefaultAttendanceGuests}
                  onDownloadGuestsWithGiftExcel={handleDownloadGuestsWithGiftExcel}
                  isLoadingGuestsWithGift={isLoadingGuestsWithGift}
                  isDownloadingGuestsWithGift={isDownloadingGuestsWithGift}
                  isShowingSameNameHaveGift={attendanceDataMode === "same-name-have-gift"}
                  sameNameWithGiftCount={sameNameGiftCounts.withGift}
                  onToggleInvite={handleAttendanceInviteToggle}
                  onToggleDone={handleAttendanceDoneToggle}
                  doneById={doneById}
                  openGiftModal={openGiftModal}
                  attendanceGiftTotals={attendanceGiftTotals}
                  formatCurrency={formatCurrency}
                  onDeleteSelected={handleDeleteSelectedAttendanceRows}
                  isDeletingSelected={isDeletingSelectedAttendanceRows}
                  guestEstimate={guestEstimate}
                  isLoadingGuestEstimate={isLoadingGuestEstimate}
                  guestEstimateError={guestEstimateError}
                  onRequestGuestEstimate={loadGuestEstimate}
                  categoryTranslations={categoryTranslations}
                  onCreateCategory={createCategory}
                  onUpdateCategory={updateCategory}
                  onDeleteCategory={deleteCategory}
                  isLoadingAttendanceGuests={isLoadingAttendanceGuests}
                  attendancePage={attendancePage}
                  attendanceTotalPages={attendanceTotalPages}
                  onAttendancePageChange={handleAttendancePageChange}
                  onDownloadGuestImportSample={handleDownloadGuestImportSample}
                  onPreviewGuestExcel={handlePreviewGuestExcel}
                  onUploadGuestExcel={handleUploadGuestExcel}
                  isUploadingGuestExcel={isUploadingGuestExcel}
                />
              </div>

              <div
                className={`absolute inset-0 ${
                  activeTab === "budget"
                    ? "visible pointer-events-auto opacity-100"
                    : "invisible pointer-events-none opacity-0"
                }`}
              >
                <BudgetSection
                  budgetText={budgetText}
                  language={language}
                  openBudgetAmountModal={openBudgetAmountModal}
                  formatCurrency={formatCurrency}
                  displayedTotals={displayedTotals}
                  handleSpendingSubmit={handleSpendingSubmit}
                  spendingForm={spendingForm}
                  setSpendingForm={setSpendingForm}
                  editingSpendingId={editingSpendingId}
                  spending={spending}
                  startEditSpending={startEditSpending}
                  requestDeleteSpendingRow={setBudgetDeleteId}
                  onDeleteSelected={deleteSelectedSpendingRows}
                  isDeletingSelected={isDeletingSelectedBudgetRows}
                  onDownloadBudgetImportSample={handleDownloadBudgetImportSample}
                  onUploadBudgetFile={handleUploadBudgetFile}
                  isUploadingBudgetFile={isUploadingBudgetFile}
                />
              </div>

              <div
                className={`absolute inset-0 ${
                  activeTab === "payment-package"
                    ? "visible pointer-events-auto opacity-100"
                    : "invisible pointer-events-none opacity-0"
                }`}
              >
                <PaymentPackageSection
                  language={language}
                  formatCurrency={formatCurrency}
                  isActive={activeTab === "payment-package"}
                  onSessionExpired={showSessionExpiredModal}
                />
              </div>

              <div
                className={`absolute inset-0 overflow-y-auto ${
                  activeTab === "landing-page"
                    ? "visible pointer-events-auto opacity-100"
                    : "invisible pointer-events-none opacity-0"
                }`}
              >
                <LandingPageSection language={language} />
              </div>
            </div>
          </section>
        )}
      </div>
      <footer className="hidden fixed bottom-3 left-1/2 z-30 -translate-x-1/2 text-center text-xs font-medium tracking-wide text-[color:var(--foreground)]/80 sm:block">
        &copy; 2026 Vuth Sachakborameiy. All rights reserved.
      </footer>
      {paymentPopupOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "var(--modal-backdrop)" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-popup-title"
        >
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-[color:var(--field-border)] bg-[color:var(--surface)] shadow-[var(--modal-shadow)]">
            <div className="flex items-start justify-between gap-4 border-b border-[color:var(--divider)] px-5 py-4">
              <div>
                <h3
                  id="payment-popup-title"
                  className="text-lg font-bold text-[color:var(--foreground)]"
                >
                  {language === "kh" ? "បង់ប្រាក់ជាមុន" : "Pay first"}
                </h3>
                <p className="mt-1 text-sm leading-5 text-[color:var(--muted)]">
                  {language === "kh"
                    ? "ស្កេន QR រួចបញ្ចូលរូបថតបង់ប្រាក់ ដើម្បីផ្ញើទៅក្រុមការងារ។"
                    : "Scan the QR, then upload your payment screenshot to send it to our team."}
                </p>
              </div>
              <button
                type="button"
                onClick={closePaymentPopup}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-lg font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                aria-label={language === "kh" ? "បិទ" : "Close"}
              >
                ×
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-2 rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] p-1">
                <button
                  type="button"
                  onClick={() => setPaymentPopupTab("scan")}
                  className={`rounded-full px-3 py-2 text-sm font-semibold transition ${
                    paymentPopupTab === "scan"
                      ? "bg-[color:var(--accent)] text-white shadow-[var(--accent-shadow)]"
                      : "text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                  }`}
                >
                  {language === "kh" ? "ស្កេន QR" : "Scan QR"}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentPopupTab("upload")}
                  className={`rounded-full px-3 py-2 text-sm font-semibold transition ${
                    paymentPopupTab === "upload"
                      ? "bg-[color:var(--accent)] text-white shadow-[var(--accent-shadow)]"
                      : "text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                  }`}
                >
                  {language === "kh" ? "បញ្ចូលរូបថត" : "Upload screenshot"}
                </button>
              </div>

              {paymentPopupTab === "scan" ? (
                <div className="mt-5 grid gap-4">
                  <div className="overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-white p-3">
                    <img
                      src="/images/QR/AC.jpg"
                      alt={language === "kh" ? "QR សម្រាប់បង់ប្រាក់" : "Payment QR code"}
                      className="mx-auto max-h-[52svh] w-full rounded-xl object-contain"
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <a
                      href={PROJECT_ACCESS_PAYMENT_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-11 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] px-4 py-2.5 text-center text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                    >
                      {language === "kh" ? "បើកកម្មវិធីធនាគារ" : "Open bank app"}
                    </a>
                    <button
                      type="button"
                      onClick={() => setPaymentPopupTab("upload")}
                      className="inline-flex min-h-11 items-center justify-center rounded-full bg-[color:var(--accent)] px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:brightness-110"
                    >
                      {language === "kh" ? "បានបង់រួច" : "Payment done"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-5 grid gap-4">
                  <label className="grid cursor-pointer gap-3 rounded-2xl border border-dashed border-[color:var(--field-border)] bg-[color:var(--card)] p-5 text-center transition hover:bg-[color:var(--surface-hover)]">
                    <span className="text-sm font-semibold text-[color:var(--foreground)]">
                      {language === "kh"
                        ? "ជ្រើសរើសរូបថតបង់ប្រាក់"
                        : "Choose payment screenshot"}
                    </span>
                    <span className="text-xs leading-5 text-[color:var(--muted)]">
                      {paymentScreenshotName ||
                        (language === "kh"
                          ? "PNG, JPG ឬរូបថតពីទូរស័ព្ទ"
                          : "PNG, JPG, or a phone screenshot")}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePaymentScreenshotUpload}
                      className="sr-only"
                    />
                  </label>

                  <button
                    type="button"
                    disabled={!paymentScreenshotFile || isSendingPaymentScreenshot}
                    onClick={() => void sendPaymentScreenshotToOwner()}
                    className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-[color:var(--accent)] px-5 py-3 text-center text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.22)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none dark:disabled:bg-slate-800 dark:disabled:text-slate-500"
                  >
                    {isSendingPaymentScreenshot
                      ? language === "kh"
                        ? "កំពុងផ្ញើ..."
                        : "Sending..."
                      : language === "kh"
                        ? "ផ្ញើទៅក្រុមការងារ Telegram"
                        : "Send to our team on Telegram"}
                  </button>
                  {paymentSendNotice ? (
                    <p
                      className={`text-center text-xs font-medium leading-5 ${
                        paymentSendNotice.includes("Sent") || paymentSendNotice.includes("បានផ្ញើ")
                          ? "text-emerald-600"
                          : "text-red-600"
                      }`}
                    >
                      {paymentSendNotice}
                    </p>
                  ) : (
                    <p className="text-center text-xs leading-5 text-[color:var(--muted)]">
                      {language === "kh"
                        ? "ប្រព័ន្ធនឹងផ្ញើឈ្មោះ ទំនាក់ទំនង និងរូបថតទៅ Telegram ដោយស្វ័យប្រវត្តិ។"
                        : "This will automatically send the name, contact, and screenshot to Telegram."}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {sessionExpiredModalOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "var(--modal-backdrop)" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-expired-title"
        >
          <div className="relative w-full max-w-sm overflow-hidden rounded-[1.75rem] border border-white/50 bg-[color:var(--surface)] p-6 shadow-[0_30px_80px_rgba(15,23,42,0.32),0_10px_24px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.7)] dark:border-white/10 dark:shadow-[0_30px_80px_rgba(0,0,0,0.6),0_10px_24px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.08)]">
            <div className="pointer-events-none absolute -right-16 -top-20 h-44 w-44 rounded-full bg-amber-400/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-20 h-48 w-48 rounded-full bg-[color:var(--accent)]/15 blur-3xl" />

            <div className="relative">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[1.6rem] border border-amber-200/80 bg-gradient-to-br from-amber-100 via-amber-50 to-orange-200 text-amber-700 shadow-[0_14px_25px_rgba(217,119,6,0.26),inset_0_2px_2px_rgba(255,255,255,0.9),inset_0_-5px_10px_rgba(217,119,6,0.15)] dark:border-amber-400/20 dark:from-amber-500/25 dark:via-amber-500/15 dark:to-orange-600/20 dark:text-amber-300">
                <svg
                  viewBox="0 0 48 48"
                  className="h-11 w-11 drop-shadow-[0_3px_2px_rgba(120,53,15,0.22)]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M24 5 39 11v10c0 10.2-6.2 18.2-15 22-8.8-3.8-15-11.8-15-22V11L24 5Z" />
                  <circle cx="24" cy="23" r="8" />
                  <path d="M24 18.5V23l3.2 2.2" />
                </svg>
              </div>

              <div className="mt-5 text-center">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.22em] text-amber-600 dark:text-amber-400">
                  {language === "kh" ? "ការជូនដំណឹងសុវត្ថិភាព" : "Access notice"}
                </p>
                <h3
                  id="session-expired-title"
                  className="mt-2 text-xl font-bold tracking-tight text-[color:var(--foreground)]"
                >
                  {language === "kh" ? "កិច្ចសន្យាបានផុតកំណត់" : "Contract expired"}
                </h3>
                <p className="mt-3 text-sm leading-6 text-[color:var(--muted)]">
                  {language === "kh"
                    ? "កិច្ចសន្យាប្រើប្រាស់គេហទំព័ររបស់អ្នកបានផុតកំណត់។ សូមទាក់ទងអ្នកគ្រប់គ្រងដើម្បីបន្តការប្រើប្រាស់។"
                    : "Your website usage contract has expired. Please contact the administrator to continue using this service."}
                </p>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  data-auth-validation="skip"
                  onClick={clearAuthSession}
                  className="w-full rounded-2xl border border-white/20 bg-[color:var(--accent)] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white shadow-[0_10px_20px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.25)] transition hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 active:shadow-inner"
                >
                  {language === "kh" ? "យល់ព្រម" : "OK"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <SignOutConfirmModal
        open={signOutConfirmOpen}
        onCancel={() => setSignOutConfirmOpen(false)}
        onConfirm={handleBackToLogin}
        title={systemText[language].confirmSignOutTitle}
        message={systemText[language].confirmSignOutMessage}
        cancelLabel={systemText[language].cancel}
        confirmLabel={systemText[language].signOut}
      />
      <DeleteUploadedModal
        open={uploadedDeleteId != null}
        isDeleting={isDeletingDraft}
        onCancel={() => setUploadedDeleteId(null)}
        onConfirm={handleDeleteUploadedRow}
        title={systemText[language].confirmDeleteTitle}
        message={systemText[language].confirmDeleteUploadedMessage}
        cancelLabel={systemText[language].cancel}
        deleteLabel={systemText[language].delete}
        deletingLabel={systemText[language].deleting}
      />
      <GiftModal
        open={giftGuestId != null}
        title={guestsText[language].giftModalTitle}
        hint={guestsText[language].giftModalHint}
        cancelLabel={guestsText[language].cancel}
        saveLabel={guestsText[language].save}
        amountInput={giftAmountInput}
        onAmountChange={(nextValue) => {
          setGiftAmountInput(nextValue);
          if (!nextValue.trim() || !Number.isNaN(Number(nextValue))) {
            setGiftInputError("");
          }
        }}
        currencyInput={giftCurrencyInput}
        onCurrencyChange={setGiftCurrencyInput}
        inputError={giftInputError}
        onCancel={() => {
          setGiftGuestId(null);
          setGiftAmountInput("");
          setGiftCurrencyInput("Riel");
          setGiftInputError("");
        }}
        onSave={handleSaveGift}
      />
      <BudgetAmountModal
        open={budgetAmountModalOpen}
        title={budgetText[language].editAmountsTitle}
        hint={budgetText[language].editAmountsHint}
        planPlaceholder={budgetText[language].planAmount}
        currentPlaceholder={budgetText[language].currentAmount}
        cancelLabel={budgetText[language].cancel}
        saveLabel={budgetText[language].save}
        budgetValue={budgetDraft.budget}
        actualValue={budgetDraft.actual}
        onBudgetChange={(value) => setBudgetDraft((prev) => ({ ...prev, budget: value }))}
        onActualChange={(value) => setBudgetDraft((prev) => ({ ...prev, actual: value }))}
        onCancel={() => setBudgetAmountModalOpen(false)}
        onSave={saveBudgetAmountModal}
        inputAmountOnlyText={budgetText[language].inputAmountOnly}
        clearAmountLabel={systemText[language].clearAmount}
      />
      <BudgetDeleteModal
        open={budgetDeleteId != null}
        isDeleting={isDeletingBudgetItem}
        onCancel={() => setBudgetDeleteId(null)}
        onConfirm={deleteSpendingRow}
        title={systemText[language].confirmDeleteTitle}
        message={systemText[language].confirmDeleteBudgetMessage}
        cancelLabel={systemText[language].cancel}
        deleteLabel={systemText[language].delete}
        deletingLabel={systemText[language].deleting}
      />
      {budgetDeleteResult ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "var(--modal-backdrop)" }}
        >
          <div className="w-full max-w-sm rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-6 shadow-[var(--modal-shadow)]">
            <h3 className="text-lg font-semibold text-emerald-600">{systemText[language].success}</h3>
            <p className="mt-2 text-sm text-[color:var(--muted)]">{budgetDeleteResult}</p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                data-tab-trigger="true"
                onClick={() => setBudgetDeleteResult(null)}
                className="rounded-full bg-[color:var(--accent)] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white transition hover:brightness-110"
              >
                {systemText[language].ok}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <AttendanceStatusModal
        open={Boolean(attendanceViewStatus)}
        title={
          attendanceViewStatus
            ? `${guestsText[language].status[attendanceViewStatus]} ${guestsText[language].viewGuests}`
            : ""
        }
        noGuestsText={guestsText[language].noStatusGuests}
        guests={attendanceGuestsToView}
        onClose={() => setAttendanceViewStatus(null)}
        closeLabel={systemText[language].closeGuestList}
      />
      <MapModal
        open={mapOpen}
        locationError={locationError}
        mapsUnavailableMessage={mapsUnavailableMessage}
        selectedGuest={selectedGuest}
        mapRef={mapRef}
        onUseCurrent={handleUseCurrentLocation}
        onClose={() => setMapOpen(false)}
        title={systemText[language].pickLocation}
        hint={systemText[language].clickMapSetLocation}
        useCurrentLabel={systemText[language].useCurrent}
        closeLabel={systemText[language].close}
      />
    </div>
  );
}
